/**
 * Custom bundle PDP configurator.
 * Flat cart/add.js items + `_bundle_*` properties; theme groups in cart.
 *
 * TODO (maintainability): This file grew past ~2.6k lines (pickers, swatches/Ajax, pricing/SAVE,
 * stock, delivery, ATC, Configure draft/Confirm, option overflow, tooltip, product cache, Pass E gallery sync).
 * Drawer strip image math lives in `@theme/dev-bundle-drawer-images`. When touching a domain, prefer
 * extract into modules (drawer, cart-add, pricing, options, gallery) without a rewrite.
 *
 * @see docs/dev-custom-bundle-plan.md
 */
import { Component } from '@theme/component';
import { DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';
import {
  DRAWER_GALLERY_MAX,
  drawerProductImagesFromAjax,
  promoteDraftVariantImage,
} from '@theme/dev-bundle-drawer-images';

const COLOR_OPTION_NAMES = new Set(['color', 'colour', 'colours', 'colors', 'kolor']);

/** Max lifestyle images per component in the PDP bundle gallery (Pass E). */
const BUNDLE_GALLERY_LIFESTYLE_MAX = 2;

/**
 * CDN width for lightbox rail thumbnails built at runtime.
 * Mirrors the widest candidate that `snippets/dev-zoom-thumb-image.liquid` serves at DPR 3; keep both in sync.
 */
const RAIL_THUMB_WIDTH = 240;

/**
 * @param {string | null | undefined} name
 * @returns {boolean}
 */
function isColorOption(name) {
  return COLOR_OPTION_NAMES.has(String(name || '').toLowerCase());
}

const DELIVERY_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * @typedef {object} BundleVariant
 * @property {number} id
 * @property {string} option1
 * @property {string} option2
 * @property {string} option3
 * @property {boolean} available
 * @property {number} price
 * @property {number} compare_at_price
 * @property {string} image
 * @property {number | null} [imageId]
 * @property {string | null} inventory_management
 * @property {string | null} inventory_policy
 * @property {number | null} inventory_quantity
 * @property {number | null} [omnibus_cents]
 * @property {string} [delivery_status]
 * @property {string} [delivery_date_iso]
 * @property {number} [lead_time_days]
 * @property {number} [transit_days]
 * @property {string} [shipping_method]
 * @property {boolean} [delivery_in_stock]
 * @property {boolean} [delivery_purchasable]
 * @property {boolean} [delivery_fast]
 */

/**
 * @typedef {object} BundleComponentConfig
 * @property {string} product_handle
 * @property {string} label
 * @property {number} quantity
 * @property {boolean} [resolved]
 */

/**
 * @typedef {object} BundleConfig
 * @property {number|string} productId
 * @property {string} title
 * @property {string} handle
 * @property {string} featuredImage
 * @property {number} discountPercent
 * @property {BundleComponentConfig[]} components
 */

class DevBundleConfigurator extends Component {
  requiredRefs = [];

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {BundleConfig | null} */
  #config = null;

  /** @type {HTMLElement | null} */
  #configureTooltip = null;

  /** @type {HTMLElement | null} */
  #configureTooltipAnchor = null;

  /**
   * Shared product.js cache by handle (Configure drawer + color switch + future Dimensions).
   * @type {Map<string, { product: any, promise?: Promise<any> }>}
   */
  #productCache = new Map();

  /**
   * Draft state while Configure drawer is open. Card is untouched until Confirm.
   * @type {{
   *   componentIndex: number,
   *   handle: string,
   *   label: string,
   *   colorLabel: string,
   *   skipColor: boolean,
   *   optionNames: string[],
   *   variants: BundleVariant[],
   *   selections: Record<string, string>,
   *   productImages: { id: number | null, src: string }[],
   *   images: string[],
   * } | null}
   */
  #drawerDraft = null;

  /** @type {boolean} */
  #drawerConfirming = false;

  /** True while Confirm or draft color-switch fetch is in flight — blocks close / double-submit. */
  #drawerBusy = false;

  /** @type {ResizeObserver | null} */
  #optionOverflowRo = null;

  /** @type {number | null} */
  #optionPanelIndex = null;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #optionPanelCloseTimer = null;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    this.#config = this.#readConfig();
    if (!this.#config?.components?.length) return;

    const { signal } = this.#abortController;
    this.#initPickers(signal);
    this.#bindSwatches(signal);
    this.#syncColorLabels();
    this.#bindAtc(signal);
    this.#bindConfigureTooltip(signal);
    this.#bindConfigureDrawer(signal);
    this.#bindGalleryCardHighlight(signal);
    this.#bindGalleryZoom(signal);
    this.#bindZoomReturnTarget(signal);

    this.#recalculateAll();
  }

  /** Fill color labels from the active sibling swatch on first paint. */
  #syncColorLabels() {
    for (const card of this.querySelectorAll('[data-dev-bundle-card]')) {
      if (!(card instanceof HTMLElement)) continue;
      const handle = card.getAttribute('data-dev-bundle-current-handle');
      if (!handle) continue;
      const swatch = card.querySelector(`[data-dev-bundle-swatch][data-swatch-handle="${CSS.escape(handle)}"]`);
      const label = swatch?.getAttribute('data-swatch-color-label')?.trim();
      if (!label) continue;
      const colorLabelEl = card.querySelector('[data-dev-bundle-color-label]');
      if (colorLabelEl instanceof HTMLElement) colorLabelEl.textContent = label;
      this.#updateSelectedOptionsText(card);
    }
  }

  disconnectedCallback() {
    this.#optionOverflowRo?.disconnect();
    this.#optionOverflowRo = null;
    if (this.#optionPanelCloseTimer != null) {
      clearTimeout(this.#optionPanelCloseTimer);
      this.#optionPanelCloseTimer = null;
    }
    this.#teardownConfigureTooltip();
    this.#abortController?.abort();
    this.#abortController = null;
    super.disconnectedCallback();
  }

  /**
   * Portal tip to body + position:fixed so product-column overflow cannot clip it.
   * @param {AbortSignal} signal
   */
  #bindConfigureTooltip(signal) {
    const tip = this.querySelector('[data-dev-bundle-configure-tooltip]');
    if (!(tip instanceof HTMLElement)) return;

    const card = tip.closest('[data-dev-bundle-card]');
    const anchor = card?.querySelector('[data-dev-bundle-configure-item]');
    if (!(anchor instanceof HTMLElement)) return;

    this.#configureTooltip = tip;
    this.#configureTooltipAnchor = anchor;
    tip.classList.add('dev-bundle-card__tooltip--portal');
    document.body.appendChild(tip);

    const update = () => this.#positionConfigureTooltip();
    update();
    requestAnimationFrame(update);

    window.addEventListener('scroll', update, { capture: true, passive: true, signal });
    window.addEventListener('resize', update, { passive: true, signal });
    signal.addEventListener('abort', () => this.#teardownConfigureTooltip(), { once: true });
  }

  #positionConfigureTooltip() {
    const tip = this.#configureTooltip;
    const anchor = this.#configureTooltipAnchor;
    if (!(tip instanceof HTMLElement) || !(anchor instanceof HTMLElement)) return;
    if (!document.body.contains(anchor)) return;

    // Mobile uses the Mix bar; keep portal tip off-screen / hidden there.
    if (window.matchMedia('(max-width: 749px)').matches) {
      tip.hidden = true;
      return;
    }

    tip.hidden = false;

    const rect = anchor.getBoundingClientRect();
    const card = anchor.closest('[data-dev-bundle-card]');
    const frameTop =
      card instanceof HTMLElement ? card.getBoundingClientRect().top : rect.top;
    const tipWidth = tip.offsetWidth;
    const tipHeight = tip.offsetHeight;
    // Caret diamond protrudes ~5px; keep tip of caret above the card border.
    const gap = 12;
    const pad = 8;
    // Stay clear of sticky/static header (body --header-height) + float keyframe (-4px).
    const headerHeight =
      parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;
    const minTop = pad + headerHeight + 4;

    let left = rect.right - tipWidth;
    left = Math.min(left, window.innerWidth - tipWidth - pad);
    left = Math.max(pad, left);

    let top = frameTop - tipHeight - gap;
    let below = false;
    if (top < minTop) {
      top = rect.bottom + gap;
      below = true;
    }

    tip.style.position = 'fixed';
    tip.style.top = `${Math.round(top)}px`;
    tip.style.left = `${Math.round(left)}px`;
    tip.style.right = 'auto';
    tip.style.bottom = 'auto';
    tip.style.zIndex = '';
    tip.toggleAttribute('data-dev-bundle-tooltip-below', below);
  }

  #teardownConfigureTooltip() {
    const tip = this.#configureTooltip;
    const anchor = this.#configureTooltipAnchor;
    // Put tip back on the card (don't destroy) so theme-editor remount can re-bind.
    if (tip instanceof HTMLElement) {
      tip.hidden = true;
      tip.classList.remove('dev-bundle-card__tooltip--portal');
      tip.removeAttribute('style');
      tip.removeAttribute('data-dev-bundle-tooltip-below');
      const card = anchor?.closest('[data-dev-bundle-card]');
      if (card instanceof HTMLElement) {
        card.appendChild(tip);
      } else {
        tip.remove();
      }
    }
    this.#configureTooltip = null;
    this.#configureTooltipAnchor = null;
  }

  /** @returns {BundleConfig | null} */
  #readConfig() {
    const el = this.querySelector('[data-dev-bundle-config]');
    if (!(el instanceof HTMLScriptElement)) return null;
    try {
      return JSON.parse(el.textContent || '');
    } catch {
      return null;
    }
  }

  /** @param {AbortSignal} signal */
  #initPickers(signal) {
    for (const card of this.querySelectorAll('[data-dev-bundle-card][data-dev-bundle-variants]')) {
      if (!(card instanceof HTMLElement)) continue;
      this.#updatePicker(card);
      for (const radio of card.querySelectorAll('[data-dev-bundle-option-radio]')) {
        radio.addEventListener('change', () => this.#updatePicker(card), { signal });
      }
    }
  }

  /** @param {AbortSignal} signal */
  #bindSwatches(signal) {
    for (const swatch of this.querySelectorAll('[data-dev-bundle-swatch][data-swatch-handle]')) {
      swatch.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          if (!(swatch instanceof HTMLElement)) return;
          const handle = swatch.getAttribute('data-swatch-handle');
          const colorLabel = swatch.getAttribute('data-swatch-color-label') || '';
          const compIdx = swatch.getAttribute('data-bundle-component-index');
          const card = this.querySelector(`[data-dev-bundle-card][data-dev-bundle-component-index="${compIdx}"]`);
          if (!handle || !(card instanceof HTMLElement)) return;

          /** @type {Record<string, string>} */
          const prev = {};
          for (const r of card.querySelectorAll('[data-dev-bundle-option-radio]:checked')) {
            if (!(r instanceof HTMLInputElement)) continue;
            const idx = Number(r.getAttribute('data-dev-bundle-option-index'));
            prev[`option${idx + 1}`] = r.value;
          }
          this.#switchComponent(card, handle, prev, colorLabel);
        },
        { signal }
      );
    }
  }

  /**
   * @param {string} template
   * @param {Record<string, string | number>} vars
   * @returns {string}
   */
  #fillTemplate(template, vars) {
    let out = template || '';
    for (const [key, value] of Object.entries(vars)) {
      out = out.replaceAll(`__${key.toUpperCase()}__`, String(value));
    }
    return out;
  }

  /**
   * Fill a `__RANGE__` delivery template and wrap the date range so it does not break mid-range on narrow screens.
   * @param {HTMLElement} el
   * @param {string} template
   * @param {string} range
   */
  #setLabelWithDeliveryRange(el, template, range) {
    const parts = (template || '').split('__RANGE__');
    const before = document.createTextNode(parts[0] || '');
    const after = document.createTextNode(parts[1] || '');

    if (!range) {
      el.replaceChildren(before, after);
      return;
    }

    const rangeSpan = document.createElement('span');
    rangeSpan.className = 'dev-product-card-delivery__range';
    rangeSpan.textContent = range;
    el.replaceChildren(before, rangeSpan, after);
  }

  /**
   * Drawer "when shipped" copy: fill `__DAYS__`, wrap `__EMPHASIS__…__EMPHASIS__` in medium weight, keep `__RANGE__` nowrap.
   * @param {HTMLElement} el
   * @param {string} template
   * @param {{ days?: string | number, range?: string }} vars
   */
  #setDrawerExpectCopy(el, template, vars = {}) {
    const withDays =
      vars.days != null && vars.days !== ''
        ? this.#fillTemplate(template || '', { days: vars.days })
        : template || '';
    const range = vars.range || '';
    const emphasisParts = withDays.split('__EMPHASIS__');

    /** @param {string} segment */
    const nodesForRangeSegment = (segment) => {
      const parts = segment.split('__RANGE__');
      /** @type {Node[]} */
      const nodes = [document.createTextNode(parts[0] || '')];
      if (parts.length > 1) {
        if (range) {
          const rangeSpan = document.createElement('span');
          rangeSpan.className = 'dev-product-card-delivery__range';
          rangeSpan.textContent = range;
          nodes.push(rangeSpan);
        }
        nodes.push(document.createTextNode(parts[1] || ''));
      }
      return nodes;
    };

    if (emphasisParts.length < 2) {
      el.replaceChildren(...nodesForRangeSegment(withDays));
      return;
    }

    const emphasis = document.createElement('span');
    emphasis.className = 'dev-delivery-info-drawer__emphasis';
    emphasis.append(...nodesForRangeSegment(emphasisParts[1] || ''));
    el.replaceChildren(
      document.createTextNode(emphasisParts[0] || ''),
      emphasis,
      document.createTextNode(emphasisParts[2] || ''),
    );
  }

  /**
   * "Save X" badge: label stays Weglot-translatable; money is wrapped in `.no-translate-wg`.
   * @param {HTMLElement} badgeEl
   * @param {string} amountFormatted
   */
  #setSaveAmountBadge(badgeEl, amountFormatted) {
    const amountHtml = `<span class="no-translate-wg">${this.#escapeHtml(amountFormatted)}</span>`;
    // NBSP: inline-flex collapses regular spaces between the label text and the amount span.
    const html = this.#fillTemplate(this.dataset.saveAmountLabel || 'Save __AMOUNT__', {
      amount: amountHtml,
    }).replace(/\s*(<span class="no-translate-wg">)/, '\u00A0$1');
    badgeEl.innerHTML = html;
  }

  /**
   * @param {HTMLElement} card
   * @param {BundleComponentConfig} component
   * @returns {string}
   */
  #componentTitle(card, component) {
    return (
      card.getAttribute('data-dev-bundle-component-label')?.trim() ||
      card.querySelector('[data-dev-bundle-component-title]')?.textContent?.trim() ||
      component.label ||
      component.product_handle ||
      ''
    );
  }

  /** @param {AbortSignal} signal */
  #bindAtc(signal) {
    const btn = this.querySelector('[data-dev-bundle-atc]');
    btn?.addEventListener(
      'click',
      (event) => {
        event.preventDefault();
        this.#addToCart();
      },
      { signal }
    );
  }

  /**
   * Configure / Mix → one shared drawer. Draft until Confirm.
   * @param {AbortSignal} signal
   */
  #bindConfigureDrawer(signal) {
    const drawer = this.#getConfigureDrawer();
    if (!drawer) return;

    // Stock closeDialog awaits CSS *animations*, not our right/bottom transitions.
    // Pre-slide off-screen, then let DialogComponent finish scroll-unlock + dialog.close().
    if (typeof drawer.closeDialog === 'function' && !drawer.dataset.devBundleClosePatched) {
      const originalClose = drawer.closeDialog.bind(drawer);
      drawer.closeDialog = async () => {
        // Block dismiss while Confirm / color-switch is writing — avoids discard+write race.
        if (this.#drawerBusy && !this.#drawerConfirming) return;
        this.#closeOptionPanel({ restoreFocus: false });
        const dialog = drawer.querySelector('dialog');
        if (dialog instanceof HTMLDialogElement && dialog.open && dialog.classList.contains('dialog-visible')) {
          dialog.classList.remove('dialog-visible');
          await new Promise((resolve) => {
            const finish = () => {
              dialog.removeEventListener('transitionend', onEnd);
              resolve();
            };
            /** @param {TransitionEvent} event */
            const onEnd = (event) => {
              if (event.target !== dialog) return;
              if (event.propertyName !== 'right' && event.propertyName !== 'bottom') return;
              finish();
            };
            dialog.addEventListener('transitionend', onEnd);
            setTimeout(finish, 350);
          });
        }
        return originalClose();
      };
      drawer.dataset.devBundleClosePatched = 'true';
    }

    this.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;

        const mixBtn = target.closest('[data-dev-bundle-mix]');
        if (mixBtn instanceof HTMLElement && this.contains(mixBtn)) {
          event.preventDefault();
          // Mix CTA shortcut: open Configure for the first configurable item.
          const first = this.querySelector('[data-dev-bundle-card][data-dev-bundle-configurable]');
          if (!(first instanceof HTMLElement)) return;
          const idx = Number(first.getAttribute('data-dev-bundle-component-index'));
          if (!Number.isNaN(idx)) this.#openConfigureDrawer(idx);
          return;
        }

        const card = target.closest('[data-dev-bundle-card][data-dev-bundle-configurable]');
        if (!(card instanceof HTMLElement) || !this.contains(card)) return;
        // Title PDP link and color swatches keep their own behaviour.
        if (target.closest('[data-dev-bundle-component-link]')) return;
        if (target.closest('[data-dev-bundle-swatches]')) return;

        event.preventDefault();
        const idx = Number(card.getAttribute('data-dev-bundle-component-index'));
        if (!Number.isNaN(idx)) this.#openConfigureDrawer(idx);
      },
      { signal }
    );

    drawer
      .querySelector('[data-dev-bundle-configure-confirm]')
      ?.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          this.#confirmConfigureDrawer();
        },
        { signal }
      );

    drawer
      .querySelector('[data-dev-bundle-option-panel-back]')
      ?.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.#closeOptionPanel();
        },
        { signal }
      );

    // Escape: busy → ignore; option panel → close panel; else stock dialog close.
    drawer.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Escape') return;
        if (this.#drawerBusy) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }
        if (this.#optionPanelIndex == null) return;
        event.preventDefault();
        event.stopPropagation();
        this.#closeOptionPanel();
      },
      { capture: true, signal }
    );

    drawer.addEventListener(
      DialogCloseEvent.eventName,
      () => {
        this.#closeOptionPanel({ restoreFocus: false });
        if (this.#drawerConfirming) {
          this.#drawerConfirming = false;
          return;
        }
        this.#drawerDraft = null;
      },
      { signal }
    );
  }

  /** @returns {HTMLElement & { showDialog?: () => void, closeDialog?: () => Promise<void> } | null} */
  #getConfigureDrawer() {
    const el = this.querySelector('[data-dev-bundle-configure-drawer]');
    return el instanceof HTMLElement ? el : null;
  }

  /**
   * Fetch `/products/{handle}.js` once per handle; dedupe in-flight requests.
   * @param {string} handle
   * @returns {Promise<any>}
   */
  async #getProduct(handle) {
    const key = String(handle || '').trim();
    if (!key) throw new Error('Missing product handle');

    const cached = this.#productCache.get(key);
    if (cached?.product) return cached.product;
    if (cached?.promise) return cached.promise;

    const cartRoot = window.Shopify?.routes?.root || '/';
    const promise = fetch(`${cartRoot}products/${encodeURIComponent(key)}.js`, {
      headers: { Accept: 'application/json' },
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const product = await res.json();
        this.#productCache.set(key, { product });
        return product;
      })
      .catch((err) => {
        this.#productCache.delete(key);
        throw err;
      });

    this.#productCache.set(key, { product: null, promise });
    return promise;
  }

  /**
   * @param {number} componentIndex
   */
  #openConfigureDrawer(componentIndex) {
    const card = this.querySelector(
      `[data-dev-bundle-card][data-dev-bundle-component-index="${componentIndex}"]`
    );
    const drawer = this.#getConfigureDrawer();
    if (!(card instanceof HTMLElement) || !drawer || typeof drawer.showDialog !== 'function') return;

    const handle = card.getAttribute('data-dev-bundle-current-handle') || '';
    if (!handle) return;

    /** @type {Record<string, string>} */
    const selections = { option1: '', option2: '', option3: '' };
    for (const r of card.querySelectorAll('[data-dev-bundle-option-radio]')) {
      if (r instanceof HTMLInputElement && r.checked) {
        const idx = Number(r.getAttribute('data-dev-bundle-option-index'));
        selections[`option${idx + 1}`] = r.value;
      }
    }

    let optionNames = [];
    try {
      optionNames = JSON.parse(card.getAttribute('data-dev-bundle-option-names') || '[]');
    } catch {
      optionNames = [];
    }

    const variants = this.#parseVariants(card.getAttribute('data-dev-bundle-variants'));
    const active = this.#matchDraftVariant(variants, selections) || variants[0];
    const thumb =
      active?.image ||
      card.querySelector('[data-dev-bundle-component-image] img')?.getAttribute('src') ||
      '';

    this.#drawerDraft = {
      componentIndex,
      handle,
      label:
        card.getAttribute('data-dev-bundle-component-label')?.trim() ||
        card.querySelector('[data-dev-bundle-component-title]')?.textContent?.trim() ||
        handle,
      colorLabel: card.querySelector('[data-dev-bundle-color-label]')?.textContent?.trim() || '',
      skipColor: this.#shouldSkipColorOptions(card),
      optionNames: Array.isArray(optionNames) ? optionNames.map(String) : [],
      variants,
      selections,
      productImages: thumb ? [{ id: active?.imageId ?? null, src: thumb }] : [],
      images: thumb ? [thumb] : [],
    };

    this.#closeOptionPanel({ restoreFocus: false });
    this.#promoteDraftGallery();
    this.#renderConfigureDrawer();
    drawer.showDialog();

    // Belt-and-suspenders: stock showDialog adds `.dialog-visible` on a nested rAF.
    // If that frame is skipped (modal contention / Safari), dialog stays open at
    // `right: -width` — content can paint weirdly (Clarity macOS Safari sessions).
    // Keep focus inside the modal without a visible ring (Safari treats control
    // .focus() as :focus-visible).
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const dialog = drawer.querySelector('dialog');
        if (
          dialog instanceof HTMLDialogElement &&
          dialog.open &&
          !dialog.classList.contains('dialog-closing')
        ) {
          dialog.classList.add('dialog-visible');
          dialog.tabIndex = -1;
          dialog.focus({ preventScroll: true });
        }
      });
    });

    // Enrich gallery (and anything else) from product.js — cached, non-blocking.
    this.#enrichDrawerFromProduct(handle);
  }

  /**
   * @param {string} handle
   */
  async #enrichDrawerFromProduct(handle) {
    const draft = this.#drawerDraft;
    if (!draft || draft.handle !== handle) return;

    try {
      const product = await this.#getProduct(handle);
      if (!this.#drawerDraft || this.#drawerDraft.handle !== handle) return;

      const images = drawerProductImagesFromAjax(product);
      if (images.length) {
        this.#drawerDraft.productImages = images;
        this.#promoteDraftGallery();
        this.#renderDrawerGallery();
      }
    } catch {
      // Keep card/thumb gallery; Configure still works offline from SSR data.
    }
  }

  #renderConfigureDrawer() {
    const draft = this.#drawerDraft;
    const drawer = this.#getConfigureDrawer();
    if (!draft || !drawer) return;

    this.#renderDrawerTitle();
    this.#renderDrawerGallery();
    this.#renderDrawerPrice();
    this.#renderDrawerColor();
    this.#renderDrawerOptions();
  }

  #renderDrawerTitle() {
    const draft = this.#drawerDraft;
    const drawer = this.#getConfigureDrawer();
    const titleEl = drawer?.querySelector('[data-dev-bundle-configure-title]');
    if (!draft || !(titleEl instanceof HTMLElement)) return;

    titleEl.textContent = draft.label;
    const titleLink = titleEl.closest('a');
    if (titleLink instanceof HTMLAnchorElement) {
      titleLink.href = this.#productUrl(draft.handle);
    }
    window.NoomaWeglotChrome?.retranslateLeaves?.(titleEl);
  }

  /**
   * @param {string} handle
   * @returns {string}
   */
  #productUrl(handle) {
    const root = window.Shopify?.routes?.root || '/';
    const base = root.endsWith('/') ? root : `${root}/`;
    return `${base}products/${encodeURIComponent(handle)}`;
  }

  #renderDrawerGallery() {
    const draft = this.#drawerDraft;
    const drawer = this.#getConfigureDrawer();
    const gallery = drawer?.querySelector('[data-dev-bundle-configure-gallery]');
    if (!draft || !(gallery instanceof HTMLElement)) return;

    const images = draft.images.length ? draft.images : [];
    if (!images.length) {
      gallery.innerHTML = '';
      return;
    }

    const limited = images.slice(0, DRAWER_GALLERY_MAX);
    gallery.innerHTML = limited
      .map((src, i) => {
        const sep = src.includes('?') ? '&' : '?';
        const coverClass = i === 0 ? '' : ' dev-bundle-configure-drawer__gallery-item--cover';
        return `<div class="dev-bundle-configure-drawer__gallery-item${coverClass}"><img src="${this.#escapeAttr(src)}${sep}width=480" alt="" loading="${i === 0 ? 'eager' : 'lazy'}" decoding="async"></div>`;
      })
      .join('');
  }

  /**
   * Match draft variant then rebuild strip images via `@theme/dev-bundle-drawer-images`.
   * @returns {boolean}
   */
  #promoteDraftGallery() {
    const draft = this.#drawerDraft;
    if (!draft) return false;
    const variant = this.#matchDraftVariant(draft.variants, draft.selections) || draft.variants[0];
    return promoteDraftVariantImage(draft, variant);
  }

  #renderDrawerPrice() {
    const draft = this.#drawerDraft;
    const drawer = this.#getConfigureDrawer();
    if (!draft || !drawer) return;

    const variant = this.#matchDraftVariant(draft.variants, draft.selections) || draft.variants[0];
    const saleEl = drawer.querySelector('[data-dev-bundle-configure-price-sale]');
    const compareWrap = drawer.querySelector('[data-dev-bundle-configure-price-compare-wrap]');
    const compareEl = drawer.querySelector('[data-dev-bundle-configure-price-compare]');
    const badgeEl = drawer.querySelector('[data-dev-bundle-configure-save-badge]');
    const omnibusEl = drawer.querySelector('[data-dev-bundle-configure-omnibus]');
    const omnibusLabelEl = drawer.querySelector('[data-dev-bundle-configure-omnibus-label]');
    const omnibusValueEl = drawer.querySelector('[data-dev-bundle-configure-omnibus-value]');
    if (!(saleEl instanceof HTMLElement)) return;

    if (!variant) {
      saleEl.textContent = '';
      compareWrap?.setAttribute('hidden', '');
      badgeEl?.setAttribute('hidden', '');
      omnibusEl?.setAttribute('hidden', '');
      return;
    }

    const discountFactor = 1 - (Number(this.#config?.discountPercent) || 0) / 100;
    const saleCents = Math.round((variant.price || 0) * discountFactor);
    const compareCents = variant.compare_at_price > variant.price ? variant.compare_at_price : variant.price;

    saleEl.textContent = this.#formatCents(saleCents);
    if (compareCents > saleCents) {
      saleEl.setAttribute('data-on-sale', 'true');
    } else {
      saleEl.removeAttribute('data-on-sale');
    }

    if (compareWrap instanceof HTMLElement && compareEl instanceof HTMLElement && compareCents > saleCents) {
      compareEl.textContent = this.#formatCents(compareCents);
      compareWrap.removeAttribute('hidden');
    } else {
      if (compareEl instanceof HTMLElement) compareEl.textContent = '';
      compareWrap?.setAttribute('hidden', '');
    }

    const saved = compareCents - saleCents;
    if (badgeEl instanceof HTMLElement) {
      if (saved > 0) {
        this.#setSaveAmountBadge(badgeEl, this.#formatCents(saved));
        badgeEl.removeAttribute('hidden');
      } else {
        badgeEl.textContent = '';
        badgeEl.setAttribute('hidden', '');
      }
    }

    // Same gate as `dev-omnibus-price`: discount + SSR metafield for current market.
    const omnibusCents = typeof variant.omnibus_cents === 'number' ? variant.omnibus_cents : null;
    const showOmnibus = compareCents > saleCents && omnibusCents != null && omnibusCents > 0;
    if (omnibusEl instanceof HTMLElement) {
      if (showOmnibus) {
        if (omnibusLabelEl instanceof HTMLElement) {
          omnibusLabelEl.textContent = this.dataset.omnibusLabel || 'Lowest price in the last 30 days:';
        }
        if (omnibusValueEl instanceof HTMLElement) {
          omnibusValueEl.textContent = this.#formatCents(omnibusCents);
        }
        omnibusEl.removeAttribute('hidden');
      } else {
        omnibusEl.setAttribute('hidden', '');
      }
    }
  }

  #renderDrawerColor() {
    const draft = this.#drawerDraft;
    const drawer = this.#getConfigureDrawer();
    if (!draft || !drawer) return;

    const colorRoot = drawer.querySelector('[data-dev-bundle-configure-color]');
    const valueEl = drawer.querySelector('[data-dev-bundle-configure-color-value]');
    const swatchesHost = drawer.querySelector('[data-dev-bundle-configure-swatches]');
    const card = this.querySelector(
      `[data-dev-bundle-card][data-dev-bundle-component-index="${draft.componentIndex}"]`
    );
    const source = card?.querySelector('[data-dev-bundle-swatches]');

    if (!(colorRoot instanceof HTMLElement) || !(swatchesHost instanceof HTMLElement)) return;

    const hasSwatches = source instanceof HTMLElement && source.querySelector('[data-dev-bundle-swatch]');
    if (!hasSwatches) {
      colorRoot.hidden = true;
      swatchesHost.innerHTML = '';
      return;
    }

    colorRoot.hidden = false;
    if (valueEl instanceof HTMLElement) valueEl.textContent = draft.colorLabel || '';

    swatchesHost.innerHTML = source.innerHTML;
    for (const item of swatchesHost.querySelectorAll('[data-dev-bundle-swatch-item]')) {
      const btn = item.querySelector('[data-dev-bundle-swatch]');
      const handle = btn?.getAttribute('data-swatch-handle') || '';
      item.classList.toggle('active', handle === draft.handle);
    }

    for (const swatch of swatchesHost.querySelectorAll('[data-dev-bundle-swatch][data-swatch-handle]')) {
      swatch.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (!(swatch instanceof HTMLElement)) return;
        const handle = swatch.getAttribute('data-swatch-handle');
        const colorLabel = swatch.getAttribute('data-swatch-color-label') || '';
        if (!handle) return;
        this.#draftSwitchColor(handle, colorLabel);
      });
    }
  }

  #renderDrawerOptions() {
    const draft = this.#drawerDraft;
    const drawer = this.#getConfigureDrawer();
    const host = drawer?.querySelector('[data-dev-bundle-configure-options]');
    if (!draft || !(host instanceof HTMLElement) || !drawer) return;

    // Cascade: if current picks form an impossible/OOS combo, snap to first valid values.
    this.#normalizeDrawerSelections();

    const validByOpt = this.#draftValidValues(draft);
    const chevronTpl = drawer.querySelector('template[data-dev-bundle-option-trigger-chevron]');
    const chevronHtml =
      chevronTpl instanceof HTMLTemplateElement ? chevronTpl.innerHTML.trim() : '';
    let html = '';

    for (let optIdx = 0; optIdx < draft.optionNames.length; optIdx++) {
      const optName = draft.optionNames[optIdx];
      if (!optName) continue;
      if (draft.skipColor && isColorOption(optName)) continue;

      const values = this.#draftOptionValues(draft, optIdx);
      if (values.length < 2) continue;

      const selected = draft.selections[`option${optIdx + 1}`] || values[0];
      // Visible title is a div (like PDP) — <legend> is out-of-flow and ignores flex gap.
      html += `<fieldset class="dev-bundle-configure-drawer__option-group" data-dev-bundle-configure-option-group data-dev-bundle-option-index="${optIdx}">`;
      html += `<legend class="dev-bundle-configure-drawer__option-label-sr">${this.#escapeHtml(optName)}</legend>`;
      html += `<div class="dev-bundle-configure-drawer__option-label" aria-hidden="true">${this.#escapeHtml(optName)}</div>`;
      html += `<button type="button" class="dev-bundle-configure-drawer__option-trigger" data-dev-bundle-option-trigger data-dev-bundle-option-index="${optIdx}" aria-haspopup="dialog" aria-expanded="false">`;
      html += `<span class="dev-bundle-configure-drawer__option-trigger-value" data-dev-bundle-option-trigger-value>${this.#escapeHtml(selected)}</span>`;
      html += chevronHtml;
      html += `</button>`;
      html += `<div class="dev-bundle-configure-drawer__option-values" data-dev-bundle-configure-option-values>`;

      for (const val of values) {
        const enabled = !!validByOpt[optIdx]?.[val];
        const pressed = val === selected;
        html += `<button type="button" class="dev-bundle-configure-drawer__option-pill" data-dev-bundle-configure-option-value="${this.#escapeAttr(val)}" data-dev-bundle-option-index="${optIdx}" aria-pressed="${pressed ? 'true' : 'false'}"${enabled ? '' : ' disabled'}><span>${this.#escapeHtml(val)}</span></button>`;
      }

      html += '</div></fieldset>';
    }

    host.innerHTML = html;

    for (const btn of host.querySelectorAll('[data-dev-bundle-configure-option-value]')) {
      btn.addEventListener('click', (event) => {
        // stopPropagation + deferred re-render: replacing the clicked node mid-bubble
        // makes dialog `contains(target)` fail → false "outside click" → closes drawer.
        event.preventDefault();
        event.stopPropagation();
        if (!(btn instanceof HTMLButtonElement) || btn.disabled) return;
        const optIdx = Number(btn.getAttribute('data-dev-bundle-option-index'));
        const value = btn.getAttribute('data-dev-bundle-configure-option-value') || '';
        if (Number.isNaN(optIdx) || !value || !this.#drawerDraft) return;
        this.#selectDrawerOptionValue(optIdx, value);
      });
    }

    for (const trigger of host.querySelectorAll('[data-dev-bundle-option-trigger]')) {
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (!(trigger instanceof HTMLElement)) return;
        const optIdx = Number(trigger.getAttribute('data-dev-bundle-option-index'));
        if (!Number.isNaN(optIdx)) this.#openOptionPanel(optIdx);
      });
    }

    this.#optionOverflowRo?.disconnect();
    this.#optionOverflowRo = new ResizeObserver(() => this.#measureDrawerOptionOverflow());
    this.#optionOverflowRo.observe(host);
    requestAnimationFrame(() => this.#measureDrawerOptionOverflow());
  }

  /**
   * Same idea as PDP `VariantOptionDrawer._check`: scrollWidth vs available width.
   * Overflowing groups switch to bordered trigger → nested option panel.
   */
  #measureDrawerOptionOverflow() {
    const drawer = this.#getConfigureDrawer();
    const host = drawer?.querySelector('[data-dev-bundle-configure-options]');
    if (!(host instanceof HTMLElement)) return;

    for (const group of host.querySelectorAll('[data-dev-bundle-configure-option-group]')) {
      if (!(group instanceof HTMLElement)) continue;
      const valuesRow = group.querySelector('[data-dev-bundle-configure-option-values]');
      if (!(valuesRow instanceof HTMLElement)) {
        group.classList.remove('dev-bundle-configure-drawer__option-group--use-drawer');
        continue;
      }

      const pillCount = valuesRow.querySelectorAll('[data-dev-bundle-configure-option-value]').length;
      if (pillCount < 2) {
        group.classList.remove('dev-bundle-configure-drawer__option-group--use-drawer');
        continue;
      }

      const wasDrawer = group.classList.contains('dev-bundle-configure-drawer__option-group--use-drawer');
      if (wasDrawer) group.classList.remove('dev-bundle-configure-drawer__option-group--use-drawer');

      const prevWrap = valuesRow.style.flexWrap;
      valuesRow.style.flexWrap = 'nowrap';
      const contentWidth = valuesRow.scrollWidth;
      valuesRow.style.flexWrap = prevWrap;

      const availableWidth = group.clientWidth;
      const overflows = availableWidth > 0 && contentWidth > availableWidth;
      group.classList.toggle('dev-bundle-configure-drawer__option-group--use-drawer', overflows);
    }
  }

  /**
   * @param {number} optIdx
   * @param {string} value
   * @param {{ focusTrigger?: boolean }} [opts]
   */
  #selectDrawerOptionValue(optIdx, value, opts = {}) {
    if (!this.#drawerDraft || !value) return;
    this.#drawerDraft.selections[`option${optIdx + 1}`] = value;
    this.#normalizeDrawerSelections();
    this.#renderDrawerPrice();
    if (this.#promoteDraftGallery()) this.#renderDrawerGallery();
    requestAnimationFrame(() => {
      if (!this.#drawerDraft) return;
      this.#renderDrawerOptions();
      if (opts.focusTrigger) {
        const drawer = this.#getConfigureDrawer();
        const trigger = drawer?.querySelector(
          `[data-dev-bundle-option-trigger][data-dev-bundle-option-index="${optIdx}"]`
        );
        if (trigger instanceof HTMLElement) trigger.focus();
      }
    });
  }

  /**
   * @param {number} optIdx
   */
  #openOptionPanel(optIdx) {
    const draft = this.#drawerDraft;
    const drawer = this.#getConfigureDrawer();
    const panel = drawer?.querySelector('[data-dev-bundle-option-panel]');
    const main = drawer?.querySelector('[data-dev-bundle-configure-main]');
    const titleEl = drawer?.querySelector('[data-dev-bundle-option-panel-title]');
    const listEl = drawer?.querySelector('[data-dev-bundle-option-panel-list]');
    const tickTpl = drawer?.querySelector('template[data-dev-bundle-option-panel-tick]');
    if (!draft || !(panel instanceof HTMLElement) || !(listEl instanceof HTMLElement)) return;

    const optName = draft.optionNames[optIdx] || '';
    const values = this.#draftOptionValues(draft, optIdx);
    if (values.length < 2) return;

    const validByOpt = this.#draftValidValues(draft);
    const selected = draft.selections[`option${optIdx + 1}`] || values[0];
    const tickHtml =
      tickTpl instanceof HTMLTemplateElement ? tickTpl.innerHTML.trim() : '';

    if (titleEl instanceof HTMLElement) {
      titleEl.textContent = optName ? `${draft.label} - ${optName}` : draft.label;
    }

    let html = '';
    for (const val of values) {
      const enabled = !!validByOpt[optIdx]?.[val];
      const active = val === selected;
      const classes = [
        'dev-bundle-configure-drawer__option-panel-row',
        active ? 'dev-bundle-configure-drawer__option-panel-row--active' : '',
        enabled ? '' : 'dev-bundle-configure-drawer__option-panel-row--unavailable',
      ]
        .filter(Boolean)
        .join(' ');
      html += `<li class="dev-bundle-configure-drawer__option-panel-item" role="listitem">`;
      html += `<button type="button" class="${classes}" data-dev-bundle-option-panel-value="${this.#escapeAttr(val)}"${enabled ? '' : ' disabled'}>`;
      if (active) html += tickHtml;
      html += `<span class="dev-bundle-configure-drawer__option-panel-label">${this.#escapeHtml(val)}</span>`;
      html += `</button></li>`;
    }
    listEl.innerHTML = html;

    for (const btn of listEl.querySelectorAll('[data-dev-bundle-option-panel-value]')) {
      btn.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (!(btn instanceof HTMLButtonElement) || btn.disabled) return;
        const value = btn.getAttribute('data-dev-bundle-option-panel-value') || '';
        if (!value) return;
        // Close first (no focus restore) — select re-renders triggers, then focuses.
        this.#closeOptionPanel({ restoreFocus: false });
        this.#selectDrawerOptionValue(optIdx, value, { focusTrigger: true });
      });
    }

    if (this.#optionPanelCloseTimer != null) {
      clearTimeout(this.#optionPanelCloseTimer);
      this.#optionPanelCloseTimer = null;
    }

    this.#optionPanelIndex = optIdx;
    panel.setAttribute('aria-hidden', 'false');
    if (main instanceof HTMLElement) main.inert = true;

    for (const trigger of drawer?.querySelectorAll('[data-dev-bundle-option-trigger]') || []) {
      if (!(trigger instanceof HTMLElement)) continue;
      const idx = Number(trigger.getAttribute('data-dev-bundle-option-index'));
      trigger.setAttribute('aria-expanded', idx === optIdx ? 'true' : 'false');
    }

    // Double rAF so the closed position paints before sliding in.
    // Keep focus in the panel (not the back control) — no visible ring.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        panel.classList.add('is-open');
        panel.tabIndex = -1;
        panel.focus({ preventScroll: true });
      });
    });
  }

  /**
   * Match CSS `--dev-bundle-configure-drawer-speed` so list clear waits for the slide.
   * @returns {number}
   */
  #optionPanelTransitionMs() {
    const drawer = this.#getConfigureDrawer();
    const panel = drawer?.querySelector('[data-dev-bundle-option-panel]');
    if (!(panel instanceof HTMLElement)) return 220;
    const raw = getComputedStyle(panel).getPropertyValue('--dev-bundle-configure-drawer-speed').trim();
    const seconds = Number.parseFloat(raw);
    if (!Number.isFinite(seconds) || seconds < 0) return 220;
    return Math.round(seconds * 1000);
  }

  /**
   * @param {{ restoreFocus?: boolean }} [opts]
   */
  #closeOptionPanel(opts = {}) {
    const { restoreFocus = true } = opts;
    const prevIdx = this.#optionPanelIndex;
    const wasOpen = prevIdx != null || this.#getConfigureDrawer()
      ?.querySelector('[data-dev-bundle-option-panel]')
      ?.classList.contains('is-open');
    this.#optionPanelIndex = null;

    const drawer = this.#getConfigureDrawer();
    const panel = drawer?.querySelector('[data-dev-bundle-option-panel]');
    const main = drawer?.querySelector('[data-dev-bundle-configure-main]');
    if (main instanceof HTMLElement) main.inert = false;

    for (const trigger of drawer?.querySelectorAll('[data-dev-bundle-option-trigger]') || []) {
      if (trigger instanceof HTMLElement) trigger.setAttribute('aria-expanded', 'false');
    }

    if (panel instanceof HTMLElement) {
      panel.classList.remove('is-open');
      panel.setAttribute('aria-hidden', 'true');

      if (this.#optionPanelCloseTimer != null) clearTimeout(this.#optionPanelCloseTimer);
      this.#optionPanelCloseTimer = setTimeout(() => {
        this.#optionPanelCloseTimer = null;
        const listEl = panel.querySelector('[data-dev-bundle-option-panel-list]');
        if (listEl instanceof HTMLElement) listEl.innerHTML = '';
      }, wasOpen ? this.#optionPanelTransitionMs() : 0);
    }

    if (restoreFocus && prevIdx != null) {
      const trigger = drawer?.querySelector(
        `[data-dev-bundle-option-trigger][data-dev-bundle-option-index="${prevIdx}"]`
      );
      if (trigger instanceof HTMLElement) {
        requestAnimationFrame(() => trigger.focus());
      }
    }
  }

  /**
   * Keep draft selections on an available variant combo (same idea as `#updatePicker` reselect).
   * Impossible / sold-out values are replaced with the first still-valid option per axis.
   */
  #normalizeDrawerSelections() {
    const draft = this.#drawerDraft;
    if (!draft) return;

    // Up to 3 passes — fixing option1 can invalidate option2, etc.
    for (let pass = 0; pass < 3; pass++) {
      const validByOpt = this.#draftValidValues(draft);
      let changed = false;

      for (let optIdx = 0; optIdx < 3; optIdx++) {
        const key = `option${optIdx + 1}`;
        const current = draft.selections[key] || '';
        const values = this.#draftOptionValues(draft, optIdx);
        if (!values.length) {
          if (current) {
            draft.selections[key] = '';
            changed = true;
          }
          continue;
        }

        if (current && validByOpt[optIdx]?.[current]) continue;

        const next = values.find((val) => validByOpt[optIdx]?.[val]) || values[0] || '';
        if (next !== current) {
          draft.selections[key] = next;
          changed = true;
        }
      }

      if (!changed) break;
    }
  }

  /**
   * @param {{ variants: BundleVariant[], selections: Record<string, string>, optionNames: string[] }} draft
   * @param {number} optIdx
   * @returns {string[]}
   */
  #draftOptionValues(draft, optIdx) {
    const seen = new Set();
    /** @type {string[]} */
    const out = [];
    for (const v of draft.variants) {
      const val = this.#optionValue(v, optIdx);
      if (val && !seen.has(val)) {
        seen.add(val);
        out.push(val);
      }
    }
    return out;
  }

  /**
   * @param {{ variants: BundleVariant[], selections: Record<string, string> }} draft
   * @returns {Record<number, Record<string, boolean>>}
   */
  #draftValidValues(draft) {
    const selected = draft.selections;
    /** @type {Record<number, Record<string, boolean>>} */
    const validByOpt = { 0: {}, 1: {}, 2: {} };
    for (const optIdx of [0, 1, 2]) {
      for (const v of draft.variants) {
        if (!v.available && !this.#isContinueSelling(v)) continue;
        const ok =
          (optIdx === 0 || !selected.option1 || v.option1 === selected.option1) &&
          (optIdx === 1 || !selected.option2 || v.option2 === selected.option2) &&
          (optIdx === 2 || !selected.option3 || v.option3 === selected.option3);
        if (!ok) continue;
        const val = this.#optionValue(v, optIdx);
        if (val) validByOpt[optIdx][val] = true;
      }
    }
    return validByOpt;
  }

  /**
   * @param {BundleVariant[]} variants
   * @param {Record<string, string>} selections
   * @returns {BundleVariant | null}
   */
  #matchDraftVariant(variants, selections) {
    const matching = variants.filter(
      (v) =>
        (!selections.option1 || v.option1 === selections.option1) &&
        (!selections.option2 || v.option2 === selections.option2) &&
        (!selections.option3 || v.option3 === selections.option3)
    );
    return matching.find((v) => v.available) || matching[0] || null;
  }

  /**
   * Draft-only color sibling switch (card untouched until Confirm).
   * @param {string} newHandle
   * @param {string} colorLabel
   */
  async #draftSwitchColor(newHandle, colorLabel) {
    const draft = this.#drawerDraft;
    if (!draft || newHandle === draft.handle || this.#drawerBusy) return;

    const previousHandle = draft.handle;
    const previousSelections = { ...draft.selections };
    const previousColorLabel = draft.colorLabel;
    draft.colorLabel = colorLabel;
    draft.handle = newHandle;
    this.#renderDrawerTitle();
    this.#setDrawerBusy(true);

    const drawer = this.#getConfigureDrawer();
    const valueEl = drawer?.querySelector('[data-dev-bundle-configure-color-value]');
    if (valueEl instanceof HTMLElement) valueEl.textContent = colorLabel;

    for (const item of drawer?.querySelectorAll('[data-dev-bundle-swatch-item]') ?? []) {
      const btn = item.querySelector('[data-dev-bundle-swatch]');
      item.classList.toggle('active', btn?.getAttribute('data-swatch-handle') === newHandle);
    }

    try {
      const product = await this.#getProduct(newHandle);
      if (!this.#drawerDraft || this.#drawerDraft.handle !== newHandle) return;

      const card = this.querySelector(
        `[data-dev-bundle-card][data-dev-bundle-component-index="${draft.componentIndex}"]`
      );
      const familyMap = card instanceof HTMLElement ? this.#readFamilyVariants(card) : null;
      const familyVariants = familyMap?.[newHandle] ?? null;

      this.#drawerDraft.optionNames = (product.options ?? []).map((/** @type {any} */ o) =>
        typeof o === 'string' ? o : o.name
      );
      this.#drawerDraft.variants = this.#serializeAjaxVariants(product.variants, familyVariants);
      this.#drawerDraft.productImages = drawerProductImagesFromAjax(product);

      for (const [key, val] of Object.entries(previousSelections)) {
        if (!val) continue;
        const optIdx = Number(key.replace('option', '')) - 1;
        const name = this.#drawerDraft.optionNames[optIdx];
        if (name && this.#drawerDraft.skipColor && isColorOption(name)) continue;
        const values = this.#draftOptionValues(this.#drawerDraft, optIdx);
        this.#drawerDraft.selections[key] = values.includes(val) ? val : values[0] || '';
      }

      this.#normalizeDrawerSelections();
      this.#promoteDraftGallery();
      this.#renderDrawerTitle();
      this.#renderDrawerGallery();
      this.#renderDrawerPrice();
      this.#renderDrawerOptions();
    } catch {
      if (!this.#drawerDraft) return;
      this.#drawerDraft.handle = previousHandle;
      this.#drawerDraft.colorLabel = previousColorLabel;
      this.#drawerDraft.selections = previousSelections;
      this.#renderDrawerTitle();
      if (valueEl instanceof HTMLElement) valueEl.textContent = previousColorLabel;
      for (const item of drawer?.querySelectorAll('[data-dev-bundle-swatch-item]') ?? []) {
        const btn = item.querySelector('[data-dev-bundle-swatch]');
        item.classList.toggle('active', btn?.getAttribute('data-swatch-handle') === previousHandle);
      }
    } finally {
      this.#setDrawerBusy(false);
    }
  }

  /**
   * @param {any[]} variants
   * @param {BundleVariant[] | null} familyVariants
   * @returns {BundleVariant[]}
   */
  #serializeAjaxVariants(variants, familyVariants) {
    return (variants ?? []).map((v) => {
      const fromFamily = familyVariants?.find((f) => f.id === Number(v.id));
      // products.js often omits inventory_quantity — keep Liquid/family values for delivery + low-stock UI
      const qtyFromAjax = typeof v.inventory_quantity === 'number' ? v.inventory_quantity : null;
      const qtyFromFamily =
        typeof fromFamily?.inventory_quantity === 'number' ? fromFamily.inventory_quantity : null;
      return {
        id: Number(v.id),
        option1: v.option1 || '',
        option2: v.option2 || '',
        option3: v.option3 || '',
        available: !!v.available,
        price: Number(v.price) || 0,
        compare_at_price: Number(v.compare_at_price) || 0,
        image: v.featured_image?.src || fromFamily?.image || '',
        imageId: Number(v.featured_image?.id) || fromFamily?.imageId || null,
        inventory_management: v.inventory_management ?? fromFamily?.inventory_management ?? null,
        inventory_policy: v.inventory_policy ?? fromFamily?.inventory_policy ?? null,
        inventory_quantity: qtyFromFamily ?? qtyFromAjax,
        omnibus_cents:
          typeof fromFamily?.omnibus_cents === 'number'
            ? fromFamily.omnibus_cents
            : typeof v.omnibus_cents === 'number'
              ? v.omnibus_cents
              : null,
        delivery_status: fromFamily?.delivery_status || '',
        delivery_date_iso: fromFamily?.delivery_date_iso || '',
        lead_time_days: fromFamily?.lead_time_days || 0,
        transit_days: fromFamily?.transit_days || 0,
        shipping_method: fromFamily?.shipping_method || 'courier',
        delivery_in_stock: !!fromFamily?.delivery_in_stock,
        delivery_purchasable: fromFamily ? fromFamily.delivery_purchasable !== false : !!v.available,
        delivery_fast: !!fromFamily?.delivery_fast,
      };
    });
  }

  async #confirmConfigureDrawer() {
    const draft = this.#drawerDraft;
    const drawer = this.#getConfigureDrawer();
    if (!draft || !drawer || this.#drawerBusy) return;

    const card = this.querySelector(
      `[data-dev-bundle-card][data-dev-bundle-component-index="${draft.componentIndex}"]`
    );
    if (!(card instanceof HTMLElement)) return;

    const currentHandle = card.getAttribute('data-dev-bundle-current-handle') || '';
    // Mark busy *before* await so Escape/close cannot discard mid-write.
    this.#drawerConfirming = true;
    this.#setDrawerBusy(true);

    try {
      if (draft.handle !== currentHandle) {
        await this.#switchComponent(card, draft.handle, draft.selections, draft.colorLabel);
      } else {
        for (const [key, val] of Object.entries(draft.selections)) {
          if (!val) continue;
          const optIdx = String(Number(key.replace('option', '')) - 1);
          const radios = card.querySelectorAll(
            `[data-dev-bundle-option-radio][data-dev-bundle-option-index="${optIdx}"]`
          );
          for (const r of radios) {
            if (r instanceof HTMLInputElement && r.value === val) {
              r.checked = true;
              break;
            }
          }
        }
        this.#updatePicker(card);
      }

      this.#drawerDraft = null;
      if (typeof drawer.closeDialog === 'function') await drawer.closeDialog();
    } catch {
      this.#drawerConfirming = false;
      this.#showError(Theme.translations?.add_to_cart_error || 'Unable to update this item.');
    } finally {
      this.#setDrawerBusy(false);
    }
  }

  /**
   * Disable Confirm / dismiss controls while async draft work runs.
   * @param {boolean} busy
   */
  #setDrawerBusy(busy) {
    this.#drawerBusy = busy;
    const drawer = this.#getConfigureDrawer();
    if (!drawer) return;

    drawer.toggleAttribute('data-dev-bundle-configure-busy', busy);
    drawer.setAttribute('aria-busy', busy ? 'true' : 'false');

    const confirm = drawer.querySelector('[data-dev-bundle-configure-confirm]');
    if (confirm instanceof HTMLButtonElement) confirm.disabled = busy;

    for (const sel of [
      '[data-dev-bundle-configure-back]',
      '[data-dev-bundle-configure-close]',
      '[data-dev-bundle-option-panel-back]',
    ]) {
      const btn = drawer.querySelector(sel);
      if (btn instanceof HTMLButtonElement) btn.disabled = busy;
    }
  }

  #recalculateAll() {
    for (const card of this.querySelectorAll('[data-dev-bundle-card][data-dev-bundle-variants]')) {
      if (card instanceof HTMLElement) this.#updatePicker(card);
    }
    this.#calculatePrice();
    this.#refreshStock();
    this.#refreshBundleDelivery();
  }

  /**
   * @param {string | null} jsonStr
   * @returns {BundleVariant[]}
   */
  #parseVariants(jsonStr) {
    if (!jsonStr) return [];
    try {
      const raw = JSON.parse(jsonStr);
      if (!Array.isArray(raw)) return [];
      return raw.map((v) => ({
        id: Number(v.id),
        option1: v.option1 || '',
        option2: v.option2 || '',
        option3: v.option3 || '',
        available: !!v.available,
        price: Number(v.price) || 0,
        compare_at_price: Number(v.compare_at_price) || 0,
        image: v.image || '',
        imageId: typeof v.image_id === 'number' ? v.image_id : Number(v.image_id) || null,
        inventory_management: v.inventory_management ?? null,
        inventory_policy: v.inventory_policy ?? null,
        inventory_quantity: typeof v.inventory_quantity === 'number' ? v.inventory_quantity : null,
        omnibus_cents: typeof v.omnibus_cents === 'number' ? v.omnibus_cents : null,
        delivery_status: v.delivery_status || '',
        delivery_date_iso: v.delivery_date_iso || '',
        lead_time_days: Number(v.lead_time_days) || 0,
        transit_days: Number(v.transit_days) || 0,
        shipping_method: v.shipping_method || 'courier',
        delivery_in_stock: !!v.delivery_in_stock,
        delivery_purchasable: v.delivery_purchasable !== false,
        delivery_fast: !!v.delivery_fast,
      }));
    } catch {
      return [];
    }
  }

  /**
   * @param {HTMLElement} card
   * @returns {Record<string, BundleVariant[]> | null}
   */
  #readFamilyVariants(card) {
    const el = card.querySelector('[data-dev-bundle-family-variants]');
    if (!(el instanceof HTMLScriptElement)) return null;
    try {
      const raw = JSON.parse(el.textContent || '');
      if (!raw || typeof raw !== 'object') return null;
      /** @type {Record<string, BundleVariant[]>} */
      const out = {};
      for (const [handle, list] of Object.entries(raw)) {
        out[handle] = this.#parseVariants(JSON.stringify(list));
      }
      return out;
    } catch {
      return null;
    }
  }

  /**
   * Card variants with delivery/qty filled from SSR family map when Ajax left them empty.
   * @param {HTMLElement} card
   * @returns {BundleVariant[]}
   */
  #getCardVariants(card) {
    const variants = this.#parseVariants(card.getAttribute('data-dev-bundle-variants'));
    const handle = card.getAttribute('data-dev-bundle-current-handle') || '';
    const familyVariants = handle ? this.#readFamilyVariants(card)?.[handle] : null;
    if (!familyVariants?.length) return variants;

    return variants.map((v) => {
      const fromFamily = familyVariants.find((f) => f.id === v.id);
      if (!fromFamily) return v;
      return {
        ...v,
        inventory_quantity:
          typeof v.inventory_quantity === 'number'
            ? v.inventory_quantity
            : fromFamily.inventory_quantity,
        delivery_status: v.delivery_status || fromFamily.delivery_status,
        delivery_date_iso: v.delivery_date_iso || fromFamily.delivery_date_iso,
        lead_time_days: v.lead_time_days || fromFamily.lead_time_days,
        transit_days: v.transit_days || fromFamily.transit_days,
        shipping_method: v.shipping_method || fromFamily.shipping_method,
        delivery_in_stock: v.delivery_in_stock || fromFamily.delivery_in_stock,
        delivery_purchasable:
          fromFamily.delivery_purchasable !== false && v.delivery_purchasable !== false,
        delivery_fast: v.delivery_fast || fromFamily.delivery_fast,
      };
    });
  }

  /**
   * @param {string} iso
   * @returns {string}
   */
  #formatDeliveryRange(iso) {
    if (!iso) return '';
    const end = new Date(`${iso}T12:00:00`);
    if (Number.isNaN(end.getTime())) return '';
    return window.NoomaBusinessDays.formatDeliveryRange(end, DELIVERY_MONTHS);
  }

  /**
   * Aggregate max delivery ISO across selected component variants (same idea as cart footer).
   * Bottleneck variant also drives lead days, transit, and shipping method in the shared drawer.
   */
  #refreshBundleDelivery() {
    const root = document.querySelector('[data-dev-bundle-delivery-root]');
    if (!(root instanceof HTMLElement)) return;

    const config = this.#config;
    if (!config) return;

    /** @type {BundleVariant | null} */
    let bottleneck = null;
    let allInStock = true;
    let missing = false;
    /** Lowest qty among selected low_stock components (for "Only X left"). */
    let lowStockCount = null;

    for (let i = 0; i < config.components.length; i++) {
      const c = config.components[i];
      if (!c?.product_handle) continue;
      // Unpublished / unresolved component: do not compute delivery from a partial set
      if (c.resolved === false) {
        missing = true;
        break;
      }

      const card = this.querySelector(`[data-dev-bundle-card][data-dev-bundle-component-index="${i}"]`);
      if (!(card instanceof HTMLElement)) {
        missing = true;
        break;
      }

      const hidden = this.querySelector(`[data-dev-bundle-variant-hidden][data-dev-bundle-component-index="${i}"]`);
      const variants = this.#getCardVariants(card);
      if (!(hidden instanceof HTMLInputElement) || !hidden.value) {
        missing = true;
        break;
      }

      const variant = variants.find((v) => String(v.id) === hidden.value);
      if (!variant?.delivery_date_iso || variant.delivery_purchasable === false) {
        missing = true;
        break;
      }

      if (!variant.delivery_in_stock) {
        allInStock = false;
      }

      const qty = typeof variant.inventory_quantity === 'number' ? variant.inventory_quantity : null;
      const threshold = Number(root.dataset.devBundleInventoryThreshold) || 10;
      const isLowStock =
        variant.delivery_in_stock &&
        qty !== null &&
        qty > 0 &&
        (variant.delivery_status === 'low_stock' || qty <= threshold);

      if (isLowStock) {
        lowStockCount = lowStockCount === null ? qty : Math.min(lowStockCount, qty);
      }

      if (!bottleneck || variant.delivery_date_iso > bottleneck.delivery_date_iso) {
        bottleneck = variant;
      }
    }

    const inner = root.querySelector('[data-dev-bundle-delivery-inner]');
    const rowInStock = root.querySelector('[data-dev-bundle-delivery-row="in_stock"]');
    const rowLowStock = root.querySelector('[data-dev-bundle-delivery-row="low_stock"]');
    const rowBackorder = root.querySelector('[data-dev-bundle-delivery-row="back_order"]');

    if (missing || !bottleneck?.delivery_date_iso) {
      inner?.setAttribute('hidden', '');
      rowInStock?.setAttribute('hidden', '');
      rowLowStock?.setAttribute('hidden', '');
      rowBackorder?.setAttribute('hidden', '');
      return;
    }

    const range = this.#formatDeliveryRange(bottleneck.delivery_date_iso);
    const lead = Math.max(0, Number(bottleneck.lead_time_days) || 0);
    const transit = Math.max(0, Number(bottleneck.transit_days) || 0);
    const method = (bottleneck.shipping_method || 'courier').toLowerCase();
    const fill = (tpl, vars) => this.#fillTemplate(tpl || '', vars);
    const showLowStock =
      allInStock &&
      root.hasAttribute('data-dev-bundle-show-inventory') &&
      lowStockCount !== null;

    /** @param {Element | null | undefined} active */
    const showOnlyRow = (active) => {
      for (const row of [rowInStock, rowLowStock, rowBackorder]) {
        if (!(row instanceof HTMLElement)) continue;
        row.toggleAttribute('hidden', row !== active);
      }
    };

    if (showLowStock && rowLowStock instanceof HTMLElement) {
      const countEl = rowLowStock.querySelector('[data-dev-bundle-delivery-lowstock-count]');
      const suffixEl = rowLowStock.querySelector('[data-dev-bundle-delivery-lowstock-suffix]');
      if (countEl instanceof HTMLElement) {
        countEl.textContent = fill(root.dataset.devBundleFooterLowstockCountTpl || '', {
          count: lowStockCount,
        });
      }
      if (suffixEl instanceof HTMLElement) {
        this.#setLabelWithDeliveryRange(
          suffixEl,
          root.dataset.devBundleFooterLowstockSuffixTpl || '',
          range,
        );
      }
      showOnlyRow(rowLowStock);
    } else if (allInStock && rowInStock instanceof HTMLElement) {
      const statusEl = rowInStock.querySelector('[data-dev-bundle-delivery-instock-status]');
      const suffixEl = rowInStock.querySelector('[data-dev-bundle-delivery-instock-suffix]');
      if (statusEl instanceof HTMLElement) {
        statusEl.textContent = root.dataset.devBundleFooterInstockStatusTpl || '';
      }
      if (suffixEl instanceof HTMLElement) {
        this.#setLabelWithDeliveryRange(
          suffixEl,
          root.dataset.devBundleFooterInstockSuffixTpl || '',
          range,
        );
      }
      showOnlyRow(rowInStock);
    } else if (rowBackorder instanceof HTMLElement) {
      const label = rowBackorder.querySelector('[data-dev-bundle-delivery-label]');
      if (label instanceof HTMLElement) {
        this.#setLabelWithDeliveryRange(
          label,
          root.dataset.devBundleFooterBackorderTpl || '',
          range,
        );
      }
      showOnlyRow(rowBackorder);
    }
    inner?.removeAttribute('hidden');

    const drawerInstock = root.querySelector('[data-dev-bundle-drawer-instock]');
    const drawerOos = root.querySelector('[data-dev-bundle-drawer-oos]');
    if (drawerInstock instanceof HTMLElement) {
      this.#setDrawerExpectCopy(drawerInstock, root.dataset.devBundleDrawerInstockTpl || '', {
        days: lead,
        range,
      });
      drawerInstock.toggleAttribute('hidden', !allInStock);
    }
    if (drawerOos instanceof HTMLElement) {
      this.#setDrawerExpectCopy(drawerOos, root.dataset.devBundleDrawerOosTpl || '', { range });
      drawerOos.toggleAttribute('hidden', allInStock);
    }

    const transitEl = root.querySelector('[data-dev-bundle-drawer-transit]');
    if (transitEl instanceof HTMLElement) {
      transitEl.textContent = fill(root.dataset.devBundleDrawerTransitTpl || '', { days: transit });
    }

    for (const how of root.querySelectorAll('[data-dev-bundle-how]')) {
      if (!(how instanceof HTMLElement)) continue;
      const key = how.getAttribute('data-dev-bundle-how') || '';
      how.toggleAttribute('hidden', key !== method);
    }

    const shipAmount = root.dataset.freeShippingAmount?.trim() || '';
    for (const row of [rowInStock, rowLowStock, rowBackorder]) {
      if (!(row instanceof HTMLElement)) continue;
      row.dataset.deliveryEnd = bottleneck.delivery_date_iso;
      if (shipAmount) row.dataset.freeShippingAmount = shipAmount;
    }
    if (showLowStock && lowStockCount !== null && rowLowStock instanceof HTMLElement) {
      rowLowStock.dataset.deliveryCount = String(lowStockCount);
    }
    window.NoomaDeliveryI18n?.paint(root);
  }

  /**
   * @param {BundleVariant} variant
   * @param {number} idx
   * @returns {string}
   */
  #optionValue(variant, idx) {
    if (idx === 0) return variant.option1;
    if (idx === 1) return variant.option2;
    return variant.option3;
  }

  /**
   * @param {BundleVariant | undefined} variant
   * @returns {boolean}
   */
  #isContinueSelling(variant) {
    return String(variant?.inventory_policy ?? '').toLowerCase() === 'continue';
  }

  /**
   * @param {number} cents
   * @returns {string}
   */
  #formatCents(cents) {
    const format = this.dataset.moneyFormat || '{{amount}}';
    const currency = this.dataset.currency || 'EUR';
    return formatMoney(cents, format, currency, { withoutTrailingZeros: true });
  }

  /** @param {HTMLElement} card */
  #updatePicker(card) {
    const variants = this.#parseVariants(card.getAttribute('data-dev-bundle-variants'));
    const compIndex = card.getAttribute('data-dev-bundle-component-index');
    const hidden = this.querySelector(
      `[data-dev-bundle-variant-hidden][data-dev-bundle-component-index="${compIndex}"]`
    );
    if (!(hidden instanceof HTMLInputElement)) return;

    /** @type {Record<string, string>} */
    const selected = { option1: '', option2: '', option3: '' };
    for (const r of card.querySelectorAll('[data-dev-bundle-option-radio]')) {
      if (r instanceof HTMLInputElement && r.checked) {
        const idx = Number(r.getAttribute('data-dev-bundle-option-index'));
        selected[`option${idx + 1}`] = r.value;
      }
    }

    const matching = variants.filter(
      (v) =>
        (selected.option1 === '' || v.option1 === selected.option1) &&
        (selected.option2 === '' || v.option2 === selected.option2) &&
        (selected.option3 === '' || v.option3 === selected.option3)
    );

    /** @type {Record<number, Record<string, boolean>>} */
    const validByOpt = { 0: {}, 1: {}, 2: {} };
    for (const optIdx of [0, 1, 2]) {
      for (const v of variants) {
        if (!v.available) continue;
        const ok =
          (optIdx === 0 || selected.option1 === '' || v.option1 === selected.option1) &&
          (optIdx === 1 || selected.option2 === '' || v.option2 === selected.option2) &&
          (optIdx === 2 || selected.option3 === '' || v.option3 === selected.option3);
        if (!ok) continue;
        const val = this.#optionValue(v, optIdx);
        if (val) validByOpt[optIdx][val] = true;
      }
    }

    for (const r of card.querySelectorAll('[data-dev-bundle-option-radio]')) {
      if (!(r instanceof HTMLInputElement)) continue;
      const optIdx = Number(r.getAttribute('data-dev-bundle-option-index'));
      const val = r.getAttribute('data-dev-bundle-option-value') || '';
      // Visual state via CSS: input:disabled + .dev-bundle-card__option-swatch
      r.disabled = !validByOpt[optIdx]?.[val];
    }

    const available = matching.filter((v) => v.available);
    const selectedVariant = available.length === 1 ? available[0] : available[0] || matching[0] || null;

    if (selectedVariant && available.some((v) => v.id === selectedVariant.id)) {
      hidden.value = String(selectedVariant.id);
    } else if (available.length === 1 && available[0]) {
      hidden.value = String(available[0].id);
    } else if (variants.length === 1 && variants[0]) {
      hidden.value = String(variants[0].id);
    } else {
      const stillOk = available.find((v) => String(v.id) === hidden.value);
      if (!stillOk) hidden.value = available[0] ? String(available[0].id) : '';
    }

    const active = variants.find((v) => String(v.id) === hidden.value) || selectedVariant;
    this.#updateCardImage(card, active);
    this.#updateSelectedOptionsText(card, active);

    const needsReselect = [...card.querySelectorAll('[data-dev-bundle-option-radio]')].some(
      (r) => r instanceof HTMLInputElement && r.checked && r.disabled
    );
    if (needsReselect) {
      for (const group of card.querySelectorAll('[data-dev-bundle-option-values]')) {
        const firstEnabled = group.querySelector('[data-dev-bundle-option-radio]:not([disabled])');
        const checked = group.querySelector('[data-dev-bundle-option-radio]:checked');
        if (checked instanceof HTMLInputElement && checked.disabled && firstEnabled instanceof HTMLInputElement) {
          firstEnabled.checked = true;
        }
      }
      this.#updatePicker(card);
      return;
    }

    this.#calculatePrice();
    this.#refreshStock();
    this.#refreshBundleDelivery();
    this.#emitSelectionChange(card);
  }

  /**
   * Notify Dimensions tabs (and other listeners) of the active component variant.
   * @param {HTMLElement} card
   */
  #emitSelectionChange(card) {
    const componentIndex = Number(card.getAttribute('data-dev-bundle-component-index'));
    const handle = card.getAttribute('data-dev-bundle-current-handle') || '';
    const hidden = this.querySelector(
      `[data-dev-bundle-variant-hidden][data-dev-bundle-component-index="${componentIndex}"]`
    );
    const variantId =
      hidden instanceof HTMLInputElement ? parseInt(hidden.value, 10) : NaN;
    if (!Number.isFinite(componentIndex) || !handle || !Number.isFinite(variantId) || !variantId) return;

    const safeHandle = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(handle) : handle;
    const swatch = handle
      ? card.querySelector(`[data-dev-bundle-swatch][data-swatch-handle="${safeHandle}"]`)
      : null;
    // EN source for Dimensions tab `retranslateLeaves` — not live post-Weglot text.
    const colorLabel =
      swatch?.getAttribute('data-swatch-color-label')?.trim() ||
      card.querySelector('[data-dev-bundle-color-label]')?.textContent?.trim() ||
      '';

    document.documentElement.dispatchEvent(
      new CustomEvent('dev-bundle:selection-change', {
        bubbles: true,
        detail: {
          componentIndex,
          handle,
          variantId,
          colorLabel,
        },
      })
    );
  }

  /**
   * @param {HTMLElement} card
   * @param {BundleVariant | null | undefined} variant
   */
  #updateCardImage(card, variant) {
    if (!variant?.image) return;
    const imageEl = card.querySelector('[data-dev-bundle-component-image]');
    if (!(imageEl instanceof HTMLElement)) return;
    const alt =
      card.querySelector('[data-dev-bundle-component-title]')?.textContent?.trim()?.replace(/"/g, '&quot;') || '';
    const thumbSrc = this.#cdnWidthUrl(variant.image, 240);
    imageEl.innerHTML = `<img src="${this.#escapeAttr(thumbSrc)}" alt="${alt}" width="80" height="106" loading="lazy">`;

    const idx = Number(card.getAttribute('data-dev-bundle-component-index'));
    if (Number.isFinite(idx)) {
      this.#syncGalleryPackshot(
        idx,
        variant.image,
        card.querySelector('[data-dev-bundle-component-title]')?.textContent?.trim() || '',
        variant.imageId
      );
    }
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getBundleGallery() {
    const el = document.querySelector('[data-dev-bundle-gallery]');
    return el instanceof HTMLElement ? el : null;
  }

  /**
   * Packshot hover → highlight matching summary card (same `data-dev-bundle-component-index`).
   * @param {AbortSignal} signal
   */
  #bindGalleryCardHighlight(signal) {
    const gallery = this.#getBundleGallery();
    if (!gallery) return;

    for (const packshot of gallery.querySelectorAll('[data-dev-bundle-gallery-packshot]')) {
      if (!(packshot instanceof HTMLElement)) continue;
      const index = packshot.getAttribute('data-dev-bundle-component-index');
      if (index == null) continue;

      packshot.addEventListener(
        'pointerenter',
        () => {
          this.#setGalleryCardHighlight(index);
        },
        { signal }
      );
      packshot.addEventListener(
        'pointerleave',
        () => {
          this.#setGalleryCardHighlight(null);
        },
        { signal }
      );
    }
  }

  /**
   * @param {string | null} index
   */
  #setGalleryCardHighlight(index) {
    for (const card of this.querySelectorAll('[data-dev-bundle-card]')) {
      if (!(card instanceof HTMLElement)) continue;
      if (index != null && card.getAttribute('data-dev-bundle-component-index') === index) {
        card.setAttribute('data-dev-bundle-highlighted', '');
      } else {
        card.removeAttribute('data-dev-bundle-highlighted');
      }
    }
  }

  /**
   * Open classic PDP zoom-dialog from packshot / lifestyle tiles (delegated).
   * @param {AbortSignal} signal
   */
  #bindGalleryZoom(signal) {
    const gallery = this.#getBundleGallery();
    if (!gallery?.hasAttribute('data-dev-bundle-zoom-dialog-id')) return;

    gallery.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;
        const zoomTarget = target.closest('[data-dev-bundle-zoom-target]');
        if (!(zoomTarget instanceof HTMLElement) || !gallery.contains(zoomTarget)) return;

        const dialogId = gallery.getAttribute('data-dev-bundle-zoom-dialog-id');
        if (!dialogId) return;
        const zoomDialog = document.getElementById(dialogId);
        if (!zoomDialog || typeof /** @type {any} */ (zoomDialog).open !== 'function') return;

        const index = this.#computeBundleZoomIndex(zoomDialog, zoomTarget);
        if (index < 0) return;

        event.preventDefault();
        event.stopPropagation();
        /** @type {any} */ (zoomDialog).open(index, event);
      },
      { signal }
    );
  }

  /**
   * Answer the dialog's request for a close-morph return element.
   *
   * `zoom-dialog.js` derives its own return target from a `media-gallery` host, which the bundle
   * does not have, so it asks instead. Resolving by data attributes rather than by list position
   * keeps this correct even after a colour swap changes how many lifestyle items exist.
   *
   * @param {AbortSignal} signal
   */
  #bindZoomReturnTarget(signal) {
    const gallery = this.#getBundleGallery();
    const dialogId = gallery?.getAttribute('data-dev-bundle-zoom-dialog-id');
    if (!dialogId) return;

    const zoomDialog = document.getElementById(dialogId);
    if (!zoomDialog) return;

    zoomDialog.addEventListener(
      'zoom-dialog:return-target',
      (event) => {
        const detail = /** @type {any} */ (event).detail;
        // Never override a target another listener already supplied.
        if (!detail || detail.target) return;
        detail.target = this.#resolveZoomReturnTarget(detail.item);
      },
      { signal }
    );
  }

  /**
   * Gallery tile matching a zoom-dialog media item.
   * @param {unknown} item - The dialog media element the user is looking at.
   * @returns {HTMLElement | null}
   */
  #resolveZoomReturnTarget(item) {
    const gallery = this.#getBundleGallery();
    if (!gallery || !(item instanceof HTMLElement)) return null;

    const kind = item.getAttribute('data-dev-bundle-zoom-kind');
    const componentIndex = item.getAttribute('data-dev-bundle-component-index');
    if (!kind || componentIndex == null) return null;

    let selector = `[data-dev-bundle-gallery-packshot][data-dev-bundle-component-index="${componentIndex}"]`;
    if (kind !== 'packshot') {
      const slot = item.getAttribute('data-dev-bundle-lifestyle-slot');
      if (slot == null) return null;
      selector = `[data-dev-bundle-gallery-lifestyle-item][data-dev-bundle-component-index="${componentIndex}"][data-dev-bundle-lifestyle-slot="${slot}"]`;
    }

    // Dual DOM: desktop and mobile copies both exist and one is `display: none`. Flying back to the
    // hidden copy would animate towards a zero-sized box, so pick the one actually on screen.
    for (const candidate of gallery.querySelectorAll(selector)) {
      if (candidate instanceof HTMLElement && candidate.getClientRects().length) return candidate;
    }

    return null;
  }

  /**
   * Flat index in zoom-dialog media list: packshots then lifestyles (stable data attrs).
   * @param {Element} zoomDialog
   * @param {HTMLElement} zoomTarget
   * @returns {number}
   */
  #computeBundleZoomIndex(zoomDialog, zoomTarget) {
    const kind = zoomTarget.getAttribute('data-dev-bundle-zoom-kind');
    const componentIndex = zoomTarget.getAttribute('data-dev-bundle-component-index');
    if (!kind || componentIndex == null) return -1;

    const items = [...zoomDialog.querySelectorAll('[data-dev-bundle-zoom-item]')];
    if (kind === 'packshot') {
      return items.findIndex(
        (el) =>
          el.getAttribute('data-dev-bundle-zoom-kind') === 'packshot' &&
          el.getAttribute('data-dev-bundle-component-index') === componentIndex
      );
    }

    const slot = zoomTarget.getAttribute('data-dev-bundle-lifestyle-slot');
    return items.findIndex(
      (el) =>
        el.getAttribute('data-dev-bundle-zoom-kind') === 'lifestyle' &&
        el.getAttribute('data-dev-bundle-component-index') === componentIndex &&
        el.getAttribute('data-dev-bundle-lifestyle-slot') === slot
    );
  }

  /**
   * @param {HTMLElement} gallery
   * @returns {HTMLElement | null}
   */
  #getZoomDialog(gallery) {
    const id = gallery.getAttribute('data-dev-bundle-zoom-dialog-id');
    if (!id) return null;
    const el = document.getElementById(id);
    return el instanceof HTMLElement ? el : null;
  }

  /**
   * Build a Shopify CDN URL at an exact width.
   * Variant JSON / Ajax often already include `?width=480` — appending another width is ignored
   * (first wins) and leaves zoom stuck on a 480px bitmap.
   * @param {string} imageUrl
   * @param {number} width
   * @returns {string}
   */
  #cdnWidthUrl(imageUrl, width) {
    if (!imageUrl) return '';
    const protocolRelative = imageUrl.startsWith('//');
    const absolute = protocolRelative ? `https:${imageUrl}` : imageUrl;
    try {
      const url = new URL(absolute, window.location.origin);
      url.searchParams.delete('width');
      url.searchParams.delete('height');
      url.searchParams.set('width', String(width));
      if (protocolRelative) return url.href.replace(/^https?:/, '');
      // Keep root-relative / same-host paths as full href; Shopify CDNs are absolute anyway.
      return url.href;
    } catch {
      const cleaned = imageUrl
        .replace(/([?&])width=\d+/gi, '$1')
        .replace(/([?&])height=\d+/gi, '$1')
        .replace(/\?&/, '?')
        .replace(/[?&]$/, '');
      const sep = cleaned.includes('?') ? '&' : '?';
      return `${cleaned}${sep}width=${width}`;
    }
  }

  /**
   * Stable CDN identity (path + non-size query) for skip-if-unchanged sync.
   * @param {string} imageUrl
   * @returns {string}
   */
  #cdnImageKey(imageUrl) {
    if (!imageUrl) return '';
    const absolute = imageUrl.startsWith('//') ? `https:${imageUrl}` : imageUrl;
    try {
      const url = new URL(absolute, window.location.origin);
      url.searchParams.delete('width');
      url.searchParams.delete('height');
      return `${url.pathname}?${url.searchParams.toString()}`;
    } catch {
      return imageUrl.replace(/([?&])width=\d+/gi, '').replace(/([?&])height=\d+/gi, '');
    }
  }

  /**
   * Stamp native aspect ratio on a zoom slide so the kadr matches the bitmap.
   * @param {HTMLElement} item
   * @param {Element | null} img
   */
  #bindZoomItemRatio(item, img) {
    if (!(item instanceof HTMLElement) || !(img instanceof HTMLImageElement)) return;
    if (item.hasAttribute('data-zoom-packshot')) return;

    const apply = () => {
      if (!img.naturalWidth || !img.naturalHeight) return;
      item.style.setProperty('--ratio', String(img.naturalWidth / img.naturalHeight));
    };

    apply();
    if (!img.complete) img.addEventListener('load', apply, { once: true });
  }

  /**
   * Update packshot image inside zoom-dialog (media + matching thumbnail).
   * @param {HTMLElement} gallery
   * @param {number} componentIndex
   * @param {string} imageUrl
   * @param {string} alt
   * @param {number | null} [imageId]
   */
  #syncZoomDialogPackshot(gallery, componentIndex, imageUrl, alt, imageId = null) {
    const zoomDialog = this.#getZoomDialog(gallery);
    if (!zoomDialog || !imageUrl) return;

    const item = zoomDialog.querySelector(
      `[data-dev-bundle-zoom-item][data-dev-bundle-zoom-kind="packshot"][data-dev-bundle-component-index="${componentIndex}"]`
    );
    if (!(item instanceof HTMLElement)) return;

    if (imageId != null && Number.isFinite(imageId)) {
      if (item.getAttribute('data-dev-bundle-zoom-image-id') === String(imageId)) return;
      item.setAttribute('data-dev-bundle-zoom-image-id', String(imageId));
    } else {
      const existingImg = item.querySelector('[data-dev-bundle-zoom-media-host] img');
      const existingSrc =
        existingImg instanceof HTMLImageElement
          ? existingImg.getAttribute('data_max_resolution') || existingImg.currentSrc || existingImg.src
          : '';
      if (existingSrc && this.#cdnImageKey(existingSrc) === this.#cdnImageKey(imageUrl)) return;
      item.removeAttribute('data-dev-bundle-zoom-image-id');
    }

    // Use max CDN width as src (same as zoom-dialog loadHighResolutionImage).
    const highRes = this.#cdnWidthUrl(imageUrl, 3840);
    const mediaHost = item.querySelector('[data-dev-bundle-zoom-media-host]');
    if (mediaHost instanceof HTMLElement) {
      mediaHost.innerHTML = `<img class="product-media__image" src="${this.#escapeAttr(highRes)}" alt="${this.#escapeAttr(alt)}" loading="lazy" sizes="100vw" data_max_resolution="${this.#escapeAttr(highRes)}" ref="image">`;
      this.#bindZoomItemRatio(item, mediaHost.querySelector('img'));
    }

    const thumb = zoomDialog.querySelector(
      `[data-dev-bundle-zoom-thumb][data-dev-bundle-zoom-kind="packshot"][data-dev-bundle-component-index="${componentIndex}"]`
    );
    if (thumb instanceof HTMLElement) {
      const thumbSrc = this.#cdnWidthUrl(imageUrl, RAIL_THUMB_WIDTH);
      thumb.innerHTML = `<img src="${this.#escapeAttr(thumbSrc)}" alt="${this.#escapeAttr(alt)}" loading="lazy" sizes="80px">`;
      if (alt) thumb.setAttribute('aria-label', alt);
      else thumb.removeAttribute('aria-label');
    }
  }

  /**
   * Rebuild lifestyle entries in zoom-dialog for one component (count may change).
   * @param {HTMLElement} gallery
   * @param {number} componentIndex
   * @param {string[]} urls
   * @param {string} alt
   */
  #syncZoomDialogLifestyle(gallery, componentIndex, urls, alt) {
    const zoomDialog = this.#getZoomDialog(gallery);
    if (!zoomDialog) return;

    const mediaList = zoomDialog.querySelector('[data-dev-bundle-zoom-media-list]');
    const thumbnails = zoomDialog.querySelector('[ref="thumbnails"]');
    if (!(mediaList instanceof HTMLElement)) return;

    for (const node of mediaList.querySelectorAll(
      `[data-dev-bundle-zoom-item][data-dev-bundle-zoom-kind="lifestyle"][data-dev-bundle-component-index="${componentIndex}"]`
    )) {
      node.remove();
    }
    if (thumbnails instanceof HTMLElement) {
      for (const node of thumbnails.querySelectorAll(
        `[data-dev-bundle-zoom-thumb][data-dev-bundle-zoom-kind="lifestyle"][data-dev-bundle-component-index="${componentIndex}"]`
      )) {
        node.remove();
      }
    }

    /** @type {Element | null} */
    let mediaInsertBefore = null;
    for (const item of mediaList.querySelectorAll(
      '[data-dev-bundle-zoom-item][data-dev-bundle-zoom-kind="lifestyle"]'
    )) {
      const idx = Number(item.getAttribute('data-dev-bundle-component-index'));
      if (Number.isFinite(idx) && idx > componentIndex) {
        mediaInsertBefore = item;
        break;
      }
    }

    /** @type {Element | null} */
    let thumbInsertBefore = null;
    if (thumbnails instanceof HTMLElement) {
      for (const thumb of thumbnails.querySelectorAll(
        '[data-dev-bundle-zoom-thumb][data-dev-bundle-zoom-kind="lifestyle"]'
      )) {
        const idx = Number(thumb.getAttribute('data-dev-bundle-component-index'));
        if (Number.isFinite(idx) && idx > componentIndex) {
          thumbInsertBefore = thumb;
          break;
        }
      }
    }

    const mediaFrag = document.createDocumentFragment();
    const thumbFrag = document.createDocumentFragment();

    for (let slot = 0; slot < urls.length; slot++) {
      const url = urls[slot];
      const highRes = this.#cdnWidthUrl(url, 3840);
      const thumbSrc = this.#cdnWidthUrl(url, RAIL_THUMB_WIDTH);

      const li = document.createElement('li');
      li.id = `bundle-zoom-lifestyle-${componentIndex}-${slot}`;
      li.className =
        'product-media-container product-media-container--image product-media-container--zoomable media-fit-contain';
      li.setAttribute('ref', 'media[]');
      li.setAttribute('data-dev-bundle-zoom-item', '');
      li.setAttribute('data-dev-bundle-zoom-kind', 'lifestyle');
      li.setAttribute('data-dev-bundle-component-index', String(componentIndex));
      li.setAttribute('data-dev-bundle-lifestyle-slot', String(slot));
      li.innerHTML = `<drag-zoom-wrapper class="product-media__drag-zoom-wrapper" data-zoom-wrapper><div class="product-media" data-dev-bundle-zoom-media-host><img class="product-media__image" src="${this.#escapeAttr(highRes)}" alt="${this.#escapeAttr(alt)}" loading="lazy" sizes="100vw" data_max_resolution="${this.#escapeAttr(highRes)}" ref="image"></div></drag-zoom-wrapper>`;
      const createdImg = li.querySelector('img');
      this.#bindZoomItemRatio(li, createdImg);
      mediaFrag.appendChild(li);

      if (thumbnails instanceof HTMLElement) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'button button-unstyled dialog-thumbnails-list__thumbnail';
        if (alt) btn.setAttribute('aria-label', alt);
        btn.setAttribute('data-dev-bundle-zoom-thumb', '');
        btn.setAttribute('data-dev-bundle-zoom-kind', 'lifestyle');
        btn.setAttribute('data-dev-bundle-component-index', String(componentIndex));
        btn.setAttribute('data-dev-bundle-lifestyle-slot', String(slot));
        btn.innerHTML = `<img src="${this.#escapeAttr(thumbSrc)}" alt="${this.#escapeAttr(alt)}" loading="lazy" sizes="80px">`;
        thumbFrag.appendChild(btn);
      }
    }

    if (mediaInsertBefore) {
      mediaList.insertBefore(mediaFrag, mediaInsertBefore);
    } else {
      mediaList.appendChild(mediaFrag);
    }

    if (thumbnails instanceof HTMLElement) {
      if (thumbInsertBefore) {
        thumbnails.insertBefore(thumbFrag, thumbInsertBefore);
      } else {
        thumbnails.appendChild(thumbFrag);
      }
    }

    this.#reindexZoomDialogControls(zoomDialog);
    /** @type {any} */ (zoomDialog).updatedCallback?.();
  }

  /**
   * Re-stamp thumbnail / line-nav indices after lifestyle rebuild.
   * @param {HTMLElement} zoomDialog
   */
  #reindexZoomDialogControls(zoomDialog) {
    const items = [...zoomDialog.querySelectorAll('[data-dev-bundle-zoom-item]')];
    const total = items.length;
    zoomDialog.setAttribute('data-zoom-count', String(total));
    const thumbs = [...zoomDialog.querySelectorAll('[data-dev-bundle-zoom-thumb]')];

    for (let i = 0; i < thumbs.length; i++) {
      const thumb = thumbs[i];
      if (!(thumb instanceof HTMLElement)) continue;
      thumb.setAttribute('on:click', `/handleThumbnailClick/${i}`);
      thumb.setAttribute('on:pointerenter', `/handleThumbnailPointerEnter/${i}`);
      if (i === 0) thumb.setAttribute('aria-selected', 'true');
      else thumb.removeAttribute('aria-selected');
    }

    const lineNav = zoomDialog.querySelector('[data-dev-bundle-zoom-line-nav]');
    if (!(lineNav instanceof HTMLElement)) {
      if (total > 1) {
        const container = zoomDialog.querySelector('[data-dev-bundle-zoom-thumbs-host]');
        if (container instanceof HTMLElement) {
          const nav = document.createElement('div');
          nav.className = 'nooma-line-nav dialog-zoomed-gallery__line-nav desktop:hidden';
          nav.setAttribute('data-dev-bundle-zoom-line-nav', '');
          nav.setAttribute('role', 'group');
          nav.setAttribute('aria-label', 'Slideshow');
          container.prepend(nav);
          this.#fillZoomLineNav(nav, total);
        }
      }
      return;
    }

    if (total <= 1) {
      lineNav.remove();
      return;
    }

    this.#fillZoomLineNav(lineNav, total);
  }

  /**
   * @param {HTMLElement} lineNav
   * @param {number} total
   */
  #fillZoomLineNav(lineNav, total) {
    lineNav.innerHTML = '';
    for (let i = 0; i < total; i++) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'nooma-line-nav__segment dialog-zoomed-gallery__line-nav-segment button button-unstyled';
      btn.setAttribute('aria-label', `Slide ${i + 1} of ${total}`);
      btn.setAttribute('on:click', `/handleThumbnailClick/${i}`);
      btn.setAttribute('ref', 'dots[]');
      if (i === 0) btn.setAttribute('aria-selected', 'true');
      lineNav.appendChild(btn);
    }
  }

  /**
   * Lifestyle = 2nd and 3rd images in product.js `images` order (same as SSR).
   * @param {any} product
   * @returns {string[]}
   */
  #lifestyleUrlsFromAjaxProduct(product) {
    /** @type {string[]} */
    const urls = [];
    for (const img of product?.images || []) {
      const src = typeof img === 'string' ? img : img?.src;
      if (src) urls.push(src);
    }
    return urls.slice(1, 1 + BUNDLE_GALLERY_LIFESTYLE_MAX);
  }

  /**
   * @param {number} componentIndex
   * @param {string} imageUrl
   * @param {string} alt
   * @param {number | null} [imageId]
   */
  #syncGalleryPackshot(componentIndex, imageUrl, alt, imageId = null) {
    const gallery = this.#getBundleGallery();
    if (!gallery || !imageUrl) return;

    const src = this.#cdnWidthUrl(imageUrl, 640);
    const nextKey = this.#cdnImageKey(imageUrl);
    const html = `<img class="dev-bundle-gallery__img dev-bundle-gallery__img--packshot" src="${this.#escapeAttr(src)}" alt="${this.#escapeAttr(alt)}" loading="lazy" decoding="async" sizes="(min-width: 750px) 22vw, 50vw" width="640" height="800">`;

    for (const figure of gallery.querySelectorAll(
      `[data-dev-bundle-gallery-packshot][data-dev-bundle-component-index="${componentIndex}"]`
    )) {
      const media = figure.querySelector('[data-dev-bundle-gallery-packshot-media]');
      if (!(media instanceof HTMLElement)) continue;

      const existingImg = media.querySelector('img');
      if (
        existingImg instanceof HTMLImageElement &&
        this.#cdnImageKey(existingImg.currentSrc || existingImg.src) === nextKey
      ) {
        continue;
      }

      const zoomBtn = media.querySelector('[data-dev-bundle-zoom-target]');
      media.innerHTML = html;
      if (zoomBtn instanceof HTMLElement) media.prepend(zoomBtn);
    }

    this.#syncZoomDialogPackshot(gallery, componentIndex, imageUrl, alt, imageId);
  }

  /**
   * Component that owns the lifestyle photos for a handle — the first one using it.
   *
   * Two components in the same colour are the same product, so their lifestyle images are byte
   * identical. Only the owner renders them; the rest contribute nothing. Mirrors the SSR rule,
   * which lives in the `lifestyle_dupes` block at the top of `snippets/dev-bundle-gallery.liquid`.
   *
   * @param {string} handle
   * @returns {number} -1 when the handle is not in the configuration.
   */
  #lifestyleOwnerIndex(handle) {
    const components = this.#config?.components ?? [];
    return components.findIndex((component) => component?.product_handle === handle);
  }

  /**
   * Re-sync lifestyle tiles for every component a colour swap can have changed.
   *
   * Ownership is a property of the whole configuration, not of one component: taking the last user
   * away from a handle promotes another component to owner, and adding a user demotes one. Only
   * components sitting on the two handles involved can flip, so the set stays small — a blanket
   * pass over every component would rebuild untouched tiles and flash them. Products come from
   * `#productCache`, so this costs no extra requests.
   *
   * @param {number} changedIndex
   * @param {string} previousHandle
   * @param {string} nextHandle
   */
  async #syncGalleryLifestyleAround(changedIndex, previousHandle, nextHandle) {
    const components = this.#config?.components ?? [];
    const indices = new Set([changedIndex]);

    for (const [index, component] of components.entries()) {
      const handle = component?.product_handle;
      if (handle === previousHandle || handle === nextHandle) indices.add(index);
    }

    for (const index of indices) {
      const handle = components[index]?.product_handle;
      if (!handle) continue;

      const card = this.querySelector(`[data-dev-bundle-card][data-dev-bundle-component-index="${index}"]`);
      const alt = card?.querySelector('[data-dev-bundle-component-title]')?.textContent?.trim() || '';

      try {
        this.#syncGalleryLifestyle(index, await this.#getProduct(handle), alt);
      } catch {
        // A failed fetch leaves this component's tiles as they are; never blank the gallery.
      }
    }
  }

  /**
   * Replace lifestyle tiles for one component (color sibling switch).
   * Updates desktop grid + mobile slideshow slides (same data hooks).
   * A component that does not own its handle renders no lifestyle media at all.
   * @param {number} componentIndex
   * @param {any} product
   * @param {string} alt
   */
  #syncGalleryLifestyle(componentIndex, product, alt) {
    const gallery = this.#getBundleGallery();
    if (!(gallery instanceof HTMLElement)) return;

    const handle = this.#config?.components?.[componentIndex]?.product_handle ?? '';
    // Fail open: a handle missing from the config renders its own photos rather than dropping them.
    const ownerIndex = handle ? this.#lifestyleOwnerIndex(handle) : componentIndex;
    const owns = ownerIndex < 0 || ownerIndex === componentIndex;
    const urls = owns ? this.#lifestyleUrlsFromAjaxProduct(product) : [];
    const lifestyleRoot = gallery.querySelector('[data-dev-bundle-gallery-lifestyle]');
    if (lifestyleRoot instanceof HTMLElement) {
      this.#replaceLifestyleItemsInRoot(lifestyleRoot, componentIndex, urls, alt, '(min-width: 750px) 40vw, 100vw');
    }

    this.#syncMobileLifestyleSlides(gallery, componentIndex, urls, alt);
    this.#syncZoomDialogLifestyle(gallery, componentIndex, urls, alt);
  }

  /**
   * @param {HTMLElement} lifestyleRoot
   * @param {number} componentIndex
   * @param {string[]} urls
   * @param {string} alt
   * @param {string} sizes
   */
  #replaceLifestyleItemsInRoot(lifestyleRoot, componentIndex, urls, alt, sizes) {
    const gallery = this.#getBundleGallery();
    const zoomEnabled = Boolean(gallery?.hasAttribute('data-dev-bundle-zoom-dialog-id'));

    const existing = [
      ...lifestyleRoot.querySelectorAll(
        `[data-dev-bundle-gallery-lifestyle-item][data-dev-bundle-component-index="${componentIndex}"]`
      ),
    ];
    for (const node of existing) node.remove();

    /** @type {Element | null} */
    let insertBefore = null;
    for (const item of lifestyleRoot.querySelectorAll('[data-dev-bundle-gallery-lifestyle-item]')) {
      const idx = Number(item.getAttribute('data-dev-bundle-component-index'));
      if (Number.isFinite(idx) && idx > componentIndex) {
        insertBefore = item;
        break;
      }
    }

    for (let slot = 0; slot < urls.length; slot++) {
      const url = urls[slot];
      const src = this.#cdnWidthUrl(url, 1000);
      const el = document.createElement('div');
      el.className = 'dev-bundle-gallery__lifestyle-item';
      el.setAttribute('data-dev-bundle-gallery-lifestyle-item', '');
      el.setAttribute('data-dev-bundle-component-index', String(componentIndex));
      el.setAttribute('data-dev-bundle-lifestyle-slot', String(slot));
      // Keeps the zoom open morph working after a colour swap — `zoom-dialog.js` finds its
      // transition source via `[data-zoom-source]`, so a rebuilt tile must carry it too.
      el.setAttribute('data-zoom-source', '');
      el.setAttribute('role', 'listitem');
      const zoomBtn = zoomEnabled
        ? `<button type="button" class="button button-unstyled product-media-container__zoom-button" aria-label="${this.#escapeAttr(gallery?.getAttribute('data-dev-bundle-zoom-label') || 'Zoom')}" data-dev-bundle-zoom-target data-dev-bundle-zoom-kind="lifestyle" data-dev-bundle-component-index="${componentIndex}" data-dev-bundle-lifestyle-slot="${slot}"><span class="visually-hidden">${this.#escapeHtml(gallery?.getAttribute('data-dev-bundle-zoom-open-label') || 'Open image in full screen')}</span></button>`
        : '';
      el.innerHTML = `${zoomBtn}<img class="dev-bundle-gallery__img dev-bundle-gallery__img--lifestyle" src="${this.#escapeAttr(src)}" alt="${this.#escapeAttr(alt)}" loading="lazy" decoding="async" sizes="${this.#escapeAttr(sizes)}" width="1000" height="1250">`;

      if (insertBefore) {
        lifestyleRoot.insertBefore(el, insertBefore);
      } else {
        lifestyleRoot.appendChild(el);
      }
    }
  }

  /**
   * Update mobile slideshow lifestyle slides for one component.
   * Same slot count → replace whole <img> (SSR srcset must not linger).
   * Different count → remove/create slides, reindex timelines, rebuild line-nav.
   * @param {HTMLElement} gallery
   * @param {number} componentIndex
   * @param {string[]} urls
   * @param {string} alt
   */
  #syncMobileLifestyleSlides(gallery, componentIndex, urls, alt) {
    const mobile = gallery.querySelector('[data-dev-bundle-gallery-mobile]');
    if (!(mobile instanceof HTMLElement)) return;

    const slideshow = mobile.querySelector('slideshow-component');
    const scroller = slideshow?.querySelector('slideshow-slides');
    if (!(slideshow instanceof HTMLElement) || !(scroller instanceof HTMLElement)) return;

    const items = [
      ...mobile.querySelectorAll(
        `[data-dev-bundle-gallery-lifestyle-item][data-dev-bundle-component-index="${componentIndex}"]`
      ),
    ];

    if (items.length === urls.length) {
      const zoomEnabled = gallery.hasAttribute('data-dev-bundle-zoom-dialog-id');
      const zoomLabel = gallery.getAttribute('data-dev-bundle-zoom-label') || 'Zoom';
      const zoomOpenLabel = gallery.getAttribute('data-dev-bundle-zoom-open-label') || 'Open image in full screen';

      for (const item of items) {
        if (!(item instanceof HTMLElement)) continue;
        const slot = Number(item.getAttribute('data-dev-bundle-lifestyle-slot'));
        const url = Number.isFinite(slot) ? urls[slot] : undefined;
        if (!url) continue;

        const slide = item.closest('slideshow-slide');
        slide?.removeAttribute('hidden');
        slide?.removeAttribute('reveal');
        const src = this.#cdnWidthUrl(url, 1000);
        const zoomBtn = zoomEnabled
          ? `<button type="button" class="button button-unstyled product-media-container__zoom-button" aria-label="${this.#escapeAttr(zoomLabel)}" data-dev-bundle-zoom-target data-dev-bundle-zoom-kind="lifestyle" data-dev-bundle-component-index="${componentIndex}" data-dev-bundle-lifestyle-slot="${slot}"><span class="visually-hidden">${this.#escapeHtml(zoomOpenLabel)}</span></button>`
          : '';
        item.innerHTML = `${zoomBtn}<img class="dev-bundle-gallery__img dev-bundle-gallery__img--lifestyle" src="${this.#escapeAttr(src)}" alt="${this.#escapeAttr(alt)}" loading="lazy" decoding="async" sizes="100vw" width="1000" height="1250">`;
      }
      return;
    }

    const slidesBefore = [...scroller.querySelectorAll(':scope > slideshow-slide')];
    const currentIndex = typeof /** @type {any} */ (slideshow).current === 'number' ? /** @type {any} */ (slideshow).current : 0;
    const currentSlideEl = slidesBefore[currentIndex] instanceof HTMLElement ? slidesBefore[currentIndex] : null;

    for (const slide of scroller.querySelectorAll(
      `[data-dev-bundle-gallery-lifestyle-slide][data-dev-bundle-component-index="${componentIndex}"]`
    )) {
      slide.remove();
    }

    /** @type {Element | null} */
    let insertBefore = null;
    for (const slide of scroller.querySelectorAll('[data-dev-bundle-gallery-lifestyle-slide]')) {
      const idx = Number(slide.getAttribute('data-dev-bundle-component-index'));
      if (Number.isFinite(idx) && idx > componentIndex) {
        insertBefore = slide;
        break;
      }
    }

    for (let slot = 0; slot < urls.length; slot++) {
      const slide = this.#createMobileLifestyleSlide(componentIndex, slot, urls[slot], alt);
      if (insertBefore) {
        scroller.insertBefore(slide, insertBefore);
      } else {
        scroller.appendChild(slide);
      }
    }

    this.#reindexMobileGallerySlides(slideshow, scroller);
    this.#rebuildMobileGalleryLineNav(gallery, slideshow, scroller);

    // Force refs refresh: Component MO skips pure removals (detached nodes fail #isDescendant).
    const slideshowAny = /** @type {any} */ (slideshow);
    slideshowAny.updatedCallback?.();

    const select = slideshowAny.select;
    if (typeof select !== 'function') return;

    let nextIndex = 0;
    if (currentSlideEl?.isConnected) {
      const slidesAfter = [...scroller.querySelectorAll(':scope > slideshow-slide')];
      const found = slidesAfter.indexOf(currentSlideEl);
      if (found >= 0) nextIndex = found;
    }

    select.call(slideshow, nextIndex, undefined, { animate: false });
  }

  /**
   * @param {number} componentIndex
   * @param {number} slot
   * @param {string} url
   * @param {string} alt
   * @returns {HTMLElement}
   */
  #createMobileLifestyleSlide(componentIndex, slot, url, alt) {
    const gallery = this.#getBundleGallery();
    const zoomEnabled = Boolean(gallery?.hasAttribute('data-dev-bundle-zoom-dialog-id'));
    const zoomLabel = gallery?.getAttribute('data-dev-bundle-zoom-label') || 'Zoom';
    const zoomOpenLabel = gallery?.getAttribute('data-dev-bundle-zoom-open-label') || 'Open image in full screen';

    const src = this.#cdnWidthUrl(url, 1000);
    const slide = document.createElement('slideshow-slide');
    slide.setAttribute('ref', 'slides[]');
    slide.setAttribute('aria-hidden', 'true');
    slide.className = 'dev-bundle-gallery__slide dev-bundle-gallery__slide--lifestyle';
    slide.setAttribute('data-dev-bundle-gallery-lifestyle-slide', '');
    slide.setAttribute('data-dev-bundle-component-index', String(componentIndex));
    slide.setAttribute('data-dev-bundle-lifestyle-slot', String(slot));
    slide.setAttribute('slide-id', `bundle-lifestyle-${componentIndex}-${slot}`);
    const zoomBtn = zoomEnabled
      ? `<button type="button" class="button button-unstyled product-media-container__zoom-button" aria-label="${this.#escapeAttr(zoomLabel)}" data-dev-bundle-zoom-target data-dev-bundle-zoom-kind="lifestyle" data-dev-bundle-component-index="${componentIndex}" data-dev-bundle-lifestyle-slot="${slot}"><span class="visually-hidden">${this.#escapeHtml(zoomOpenLabel)}</span></button>`
      : '';
    slide.innerHTML = `<div class="dev-bundle-gallery__lifestyle-item" data-dev-bundle-gallery-lifestyle-item data-dev-bundle-component-index="${componentIndex}" data-dev-bundle-lifestyle-slot="${slot}" data-zoom-source>${zoomBtn}<img class="dev-bundle-gallery__img dev-bundle-gallery__img--lifestyle" src="${this.#escapeAttr(src)}" alt="${this.#escapeAttr(alt)}" loading="lazy" decoding="async" sizes="100vw" width="1000" height="1250"></div>`;
    return slide;
  }

  /**
   * Keep --slideshow-timeline / aria-hidden in sync after create/remove.
   * @param {HTMLElement} slideshow
   * @param {HTMLElement} scroller
   */
  #reindexMobileGallerySlides(slideshow, scroller) {
    const slides = [...scroller.querySelectorAll(':scope > slideshow-slide')];
    const scopes = slides.map((_, i) => `--slide-${i}`).join(', ');
    const existing = slideshow.getAttribute('style') || '';
    if (existing.includes('--slideshow-timeline:')) {
      slideshow.setAttribute(
        'style',
        existing.replace(/--slideshow-timeline:\s*[^;]+;?/, `--slideshow-timeline: ${scopes};`)
      );
    } else {
      slideshow.style.setProperty('--slideshow-timeline', scopes);
    }

    for (let i = 0; i < slides.length; i++) {
      const slide = slides[i];
      if (!(slide instanceof HTMLElement)) continue;
      const style = slide.getAttribute('style') || '';
      const nextStyle = style.includes('--slideshow-timeline:')
        ? style.replace(/--slideshow-timeline:\s*[^;]+;?/, `--slideshow-timeline: --slide-${i};`)
        : `--slideshow-timeline: --slide-${i}; ${style}`.trim();
      slide.setAttribute('style', nextStyle);
      if (i === 0) {
        slide.setAttribute('aria-hidden', 'false');
      } else if (!slide.hasAttribute('reveal')) {
        slide.setAttribute('aria-hidden', 'true');
      }
    }
  }

  /**
   * Rebuild `.nooma-line-nav` dots after lifestyle slide count changes.
   * @param {HTMLElement} gallery
   * @param {HTMLElement} slideshow
   * @param {HTMLElement} scroller
   */
  #rebuildMobileGalleryLineNav(gallery, slideshow, scroller) {
    const slideCount = scroller.querySelectorAll(':scope > slideshow-slide').length;
    let controls = slideshow.querySelector('slideshow-controls');

    if (slideCount <= 1) {
      controls?.remove();
      return;
    }

    const navLabel = gallery.getAttribute('data-dev-bundle-gallery-nav-label') || '';
    const statusTpl =
      gallery.getAttribute('data-dev-bundle-gallery-slide-status') || 'Slide __INDEX__ of __LENGTH__';

    if (!(controls instanceof HTMLElement)) {
      controls = document.createElement('slideshow-controls');
      controls.className = 'nooma-line-nav';
      controls.setAttribute('pagination-position', 'center');
      slideshow.appendChild(controls);
    }

    controls.setAttribute('aria-label', navLabel);
    controls.innerHTML = '';

    const selectedIndex =
      typeof /** @type {any} */ (slideshow).current === 'number' ? /** @type {any} */ (slideshow).current : 0;

    for (let i = 0; i < slideCount; i++) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'nooma-line-nav__segment slideshow-control button button-unstyled';
      btn.setAttribute(
        'aria-label',
        statusTpl.replaceAll('__INDEX__', String(i + 1)).replaceAll('__LENGTH__', String(slideCount))
      );
      btn.setAttribute('on:click', `/select/${i}`);
      btn.setAttribute('ref', 'dots[]');
      if (i === selectedIndex) btn.setAttribute('aria-selected', 'true');
      controls.appendChild(btn);
    }
  }

  /**
   * Summary line: "Color, Size" (Figma). Color from sibling label when present.
   *
   * @param {HTMLElement} card
   * @param {BundleVariant | null | undefined} [variant]
   */
  #updateSelectedOptionsText(card, variant) {
    const el = card.querySelector('[data-dev-bundle-selected-options]');
    if (!(el instanceof HTMLElement)) return;

    let active = variant;
    if (!active) {
      const variants = this.#parseVariants(card.getAttribute('data-dev-bundle-variants'));
      const hidden = card.querySelector('[data-dev-bundle-variant-hidden]');
      const selectedId = hidden instanceof HTMLInputElement && hidden.value ? Number(hidden.value) : null;
      active = (selectedId ? variants.find((v) => v.id === selectedId) : null) ?? variants[0];
    }

    const colorLabel = card.querySelector('[data-dev-bundle-color-label]')?.textContent?.trim() || '';
    const parts = [];
    if (colorLabel) parts.push(colorLabel);

    if (active) {
      let optionNames = [];
      try {
        optionNames = JSON.parse(card.getAttribute('data-dev-bundle-option-names') || '[]');
      } catch {
        optionNames = [];
      }
      for (let i = 0; i < 3; i++) {
        const name = optionNames[i];
        if (!name || isColorOption(name)) continue;
        const val = this.#optionValue(active, i);
        if (val) parts.push(val);
      }
    }

    el.textContent = parts.join(', ');
  }

  #calculatePrice() {
    const config = this.#config;
    if (!config) return;
    const saleEl = this.querySelector('[data-dev-bundle-price-sale]');
    if (!(saleEl instanceof HTMLElement)) return;

    let totalPrice = 0;
    let totalCompareBaseline = 0;
    const discountPercent = Number(config.discountPercent) || 0;
    const discountFactor = 1 - discountPercent / 100;

    for (let i = 0; i < config.components.length; i++) {
      const c = config.components[i];
      if (!c?.product_handle) continue;
      const card = this.querySelector(`[data-dev-bundle-card][data-dev-bundle-component-index="${i}"]`);
      if (!(card instanceof HTMLElement)) continue;
      const variants = this.#parseVariants(card.getAttribute('data-dev-bundle-variants'));
      const hidden = this.querySelector(`[data-dev-bundle-variant-hidden][data-dev-bundle-component-index="${i}"]`);
      const selectedId = hidden instanceof HTMLInputElement && hidden.value ? Number(hidden.value) : null;
      const variant = (selectedId ? variants.find((v) => v.id === selectedId) : null) ?? variants[0];
      if (!variant) continue;

      const qty = Math.max(1, Math.floor(Number(c.quantity) || 1));
      const price = variant.price || 0;
      const compare = variant.compare_at_price || 0;
      const linePrice = price * qty;
      const lineCompare = (compare > price ? compare : price) * qty;
      totalPrice += linePrice;
      totalCompareBaseline += lineCompare;

      const unitSale = Math.round(price * discountFactor);
      const unitCompare = compare > price ? compare : price;
      this.#updateComponentPrice(card, unitSale, unitCompare);
    }

    const discountedTotal = Math.round(totalPrice * discountFactor);
    saleEl.textContent = this.#formatCents(discountedTotal);

    const compareWrap = this.querySelector('[data-dev-bundle-price-compare-wrap]');
    const compareEl = this.querySelector('[data-dev-bundle-price-compare]');
    const compareTotal = Math.round(totalCompareBaseline);

    if (compareWrap instanceof HTMLElement && compareEl instanceof HTMLElement && compareTotal > discountedTotal) {
      compareEl.textContent = this.#formatCents(compareTotal);
      compareWrap.removeAttribute('hidden');
      saleEl.setAttribute('data-on-sale', 'true');
    } else {
      if (compareEl instanceof HTMLElement) compareEl.textContent = '';
      compareWrap?.setAttribute('hidden', '');
      saleEl.removeAttribute('data-on-sale');
    }

    const badgeEl = this.querySelector('[data-dev-bundle-discount-badge]');
    if (!(badgeEl instanceof HTMLElement)) return;

    const savedCents = compareTotal - discountedTotal;
    if (savedCents > 0) {
      this.#setSaveAmountBadge(badgeEl, this.#formatCents(savedCents));
      badgeEl.removeAttribute('hidden');
    } else {
      badgeEl.textContent = '';
      badgeEl.setAttribute('hidden', '');
    }
  }

  /**
   * @param {HTMLElement} card
   * @param {number} saleCents
   * @param {number} compareCents
   */
  #updateComponentPrice(card, saleCents, compareCents) {
    const saleEl = card.querySelector('[data-dev-bundle-component-price-sale]');
    if (!(saleEl instanceof HTMLElement)) return;
    saleEl.textContent = this.#formatCents(saleCents);

    const compareWrap = card.querySelector('[data-dev-bundle-component-price-compare-wrap]');
    const compareEl = card.querySelector('[data-dev-bundle-component-price-compare]');

    if (compareWrap instanceof HTMLElement && compareEl instanceof HTMLElement && compareCents > saleCents) {
      compareEl.textContent = this.#formatCents(compareCents);
      compareWrap.removeAttribute('hidden');
      saleEl.setAttribute('data-on-sale', 'true');
    } else {
      if (compareEl instanceof HTMLElement) compareEl.textContent = '';
      compareWrap?.setAttribute('hidden', '');
      saleEl.removeAttribute('data-on-sale');
    }
  }

  /**
   * Prestige-aligned stock check:
   * - missing selection → red alert only on ATC (`showMessage`)
   * - sold out / insufficient → Sold out button + stock note (not red alert)
   * - missing card in DOM → skip here; ATC surfaces a clearer message
   * - no `inventory_quantity` (e.g. after Ajax color switch) → skip qty check
   *
   * @param {{ showMessage?: boolean }} [opts]
   * @returns {{ ok: boolean, reason?: string }}
   */
  #refreshStock(opts = {}) {
    const config = this.#config;
    if (!config) return { ok: false };
    const { showMessage = false } = opts;

    for (const el of this.querySelectorAll('[data-dev-bundle-sold-out]')) {
      el.removeAttribute('data-dev-bundle-sold-out');
    }

    let missingMsg = null;
    /** @type {string | null} */
    let stockNote = null;
    /** @type {'soldout' | 'insufficient' | null} */
    let stockReason = null;

    for (let i = 0; i < config.components.length; i++) {
      const c = config.components[i];
      if (!c?.product_handle) continue;
      const card = this.querySelector(`[data-dev-bundle-card][data-dev-bundle-component-index="${i}"]`);
      // Prestige: no picker in DOM → skip (resolved later at ATC)
      if (!(card instanceof HTMLElement)) continue;

      const hidden = this.querySelector(`[data-dev-bundle-variant-hidden][data-dev-bundle-component-index="${i}"]`);
      const variants = this.#parseVariants(card.getAttribute('data-dev-bundle-variants'));
      const title = this.#componentTitle(card, c);

      if (!(hidden instanceof HTMLInputElement) || !hidden.value) {
        if (!variants.some((v) => v.available)) {
          card.setAttribute('data-dev-bundle-sold-out', '');
          if (!stockNote) {
            stockNote = this.#fillTemplate(
              this.dataset.soldOutComponentLabel || '__TITLE__ is currently sold out.',
              { title }
            );
            stockReason = 'soldout';
          }
          continue;
        }
        if (!missingMsg) {
          missingMsg = this.#fillTemplate(this.dataset.selectVariantForLabel || '', { title }) ||
            this.dataset.selectVariantLabel ||
            'Select a variant';
        }
        continue;
      }

      const variant = variants.find((v) => String(v.id) === hidden.value);
      if (!variant) continue;

      // Continue selling: allow ATC even when Shopify marks the variant unavailable / qty 0
      if (this.#isContinueSelling(variant)) continue;

      if (!variant.available) {
        card.setAttribute('data-dev-bundle-sold-out', '');
        if (!stockNote) {
          stockNote = this.#fillTemplate(
            this.dataset.soldOutComponentLabel || '__TITLE__ is currently sold out.',
            { title }
          );
          stockReason = 'soldout';
        }
        continue;
      }

      if (!variant.inventory_management) continue;
      if (typeof variant.inventory_quantity !== 'number') continue;

      const componentQty = Math.max(1, Math.floor(Number(c.quantity) || 1));
      if (variant.inventory_quantity < componentQty) {
        card.setAttribute('data-dev-bundle-sold-out', '');
        if (!stockNote) {
          stockNote = this.#fillTemplate(
            this.dataset.insufficientStockLabel ||
              'Not enough stock for __TITLE__. Available: __AVAILABLE__, required: __REQUIRED__.',
            {
              title,
              available: variant.inventory_quantity,
              required: componentQty,
            }
          );
          stockReason = 'insufficient';
        }
      }
    }

    // Sold out / insufficient blocks the whole bundle — wins over missing selection
    if (stockNote) {
      this.#setButtonOutOfStock(true);
      // Prestige: sold out / insufficient intentionally skip the red alert
      if (showMessage) this.#showError('');
      this.#showStockNote(stockNote);
      return { ok: false, reason: stockReason || 'soldout' };
    }

    if (missingMsg) {
      this.#setButtonOutOfStock(false);
      this.#showStockNote('');
      if (showMessage) this.#showError(missingMsg);
      return { ok: false, reason: 'missing' };
    }

    this.#setButtonOutOfStock(false);
    this.#showError('');
    this.#showStockNote('');
    return { ok: true };
  }

  /**
   * Update ATC label without destroying slide markup.
   * @param {string} label
   */
  #setAtcLabel(label) {
    const btn = this.querySelector('[data-dev-bundle-atc]');
    if (!(btn instanceof HTMLButtonElement)) return;
    const lines = btn.querySelectorAll('[data-dev-bundle-atc-label]');
    if (lines.length) {
      for (const line of lines) {
        if (line instanceof HTMLElement) line.textContent = label;
      }
      return;
    }
    btn.textContent = label;
  }

  /**
   * Live ATC copy (may already be Weglot-translated). Prefer over SSR `data-original-text`
   * which stays EN when Shopify locale is EN and Weglot switches the page.
   * @param {HTMLButtonElement} btn
   * @returns {string}
   */
  #captureLiveAtcLabel(btn) {
    const soldOut = this.dataset.soldOutLabel?.trim() || '';
    const live = btn.querySelector('[data-dev-bundle-atc-label]')?.textContent?.trim() || '';
    if (live && live !== soldOut) return live;
    return btn.dataset.originalText?.trim() || live || '';
  }

  /**
   * @param {boolean} isOut
   */
  #setButtonOutOfStock(isOut) {
    const btn = this.querySelector('[data-dev-bundle-atc]');
    if (!(btn instanceof HTMLButtonElement)) return;

    const wasOut = btn.hasAttribute('data-dev-bundle-atc-disabled');

    // Only rewrite label on sold-out ↔ available transitions. Re-applying SSR EN
    // `data-original-text` on every stock sync wiped Weglot translations on the ATC.
    if (isOut && !wasOut) {
      const live = this.#captureLiveAtcLabel(btn);
      if (live) btn.dataset.originalTextLive = live;
      this.#setAtcLabel(this.dataset.soldOutLabel || live || '');
    } else if (!isOut && wasOut) {
      this.#setAtcLabel(
        btn.dataset.originalTextLive || btn.dataset.originalText || this.#captureLiveAtcLabel(btn) || ''
      );
    }

    btn.toggleAttribute('data-dev-bundle-atc-disabled', isOut);
    btn.toggleAttribute('disabled', isOut || btn.getAttribute('aria-busy') === 'true');
  }

  /**
   * @param {boolean} loading
   */
  #setLoading(loading) {
    const btn = this.querySelector('[data-dev-bundle-atc]');
    if (!(btn instanceof HTMLButtonElement)) return;
    btn.setAttribute('aria-busy', String(loading));
    btn.toggleAttribute('disabled', loading || btn.hasAttribute('data-dev-bundle-atc-disabled'));
  }

  /**
   * @param {string} msg
   */
  #showError(msg) {
    const wrap = this.querySelector('[data-dev-bundle-error-wrap]');
    const el = this.querySelector('[data-dev-bundle-error]');
    if (el instanceof HTMLElement) el.textContent = msg || '';
    wrap?.toggleAttribute('hidden', !msg);
  }

  /**
   * Non-alert stock messaging (sold out / insufficient). Prestige used the delivery footer.
   * @param {string} msg
   */
  #showStockNote(msg) {
    const el = this.querySelector('[data-dev-bundle-stock-note]');
    if (!(el instanceof HTMLElement)) return;
    el.textContent = msg || '';
    el.toggleAttribute('hidden', !msg);
  }

  /**
   * @param {HTMLElement} card
   * @param {string} newHandle
   * @param {Record<string, string>} previousSelections
   * @param {string} colorLabel
   */
  async #switchComponent(card, newHandle, previousSelections, colorLabel) {
    const previousHandle = card.getAttribute('data-dev-bundle-current-handle') || '';
    if (newHandle === previousHandle) return;
    const compIndex = card.getAttribute('data-dev-bundle-component-index');

    try {
      const newProduct = await this.#getProduct(newHandle);

      // Ajax products.js has no delivery metafields — merge from SSR family map when present
      const familyMap = this.#readFamilyVariants(card);
      const familyVariants = familyMap?.[newHandle] ?? null;
      const mergedVariants = this.#serializeAjaxVariants(newProduct.variants, familyVariants);

      card.setAttribute('data-dev-bundle-current-handle', newHandle);
      card.setAttribute('data-dev-bundle-variants', JSON.stringify(mergedVariants));
      card.setAttribute(
        'data-dev-bundle-option-names',
        JSON.stringify((newProduct.options ?? []).map((/** @type {any} */ o) => (typeof o === 'string' ? o : o.name)))
      );

      const displayLabel = card.getAttribute('data-dev-bundle-component-label')?.trim() || newProduct.title;
      const titleEl = card.querySelector('[data-dev-bundle-component-title]');
      if (titleEl instanceof HTMLElement) {
        titleEl.textContent = displayLabel;
        window.NoomaWeglotChrome?.retranslateLeaves?.(titleEl);
      }
      const titleLink = titleEl?.closest('a');
      if (titleLink instanceof HTMLAnchorElement) {
        titleLink.href = this.#productUrl(newHandle);
      }

      const colorLabelEl = card.querySelector('[data-dev-bundle-color-label]');
      if (colorLabelEl instanceof HTMLElement && colorLabel) colorLabelEl.textContent = colorLabel;

      for (const s of card.querySelectorAll('[data-dev-bundle-swatch]')) {
        const item = s.closest('[data-dev-bundle-swatch-item]');
        item?.classList.toggle('active', s.getAttribute('data-swatch-handle') === newHandle);
      }

      const idx = Number(compIndex);
      if (this.#config?.components[idx]) {
        this.#config.components[idx].product_handle = newHandle;
      }

      if (Number.isFinite(idx)) {
        this.#syncGalleryLifestyleAround(idx, previousHandle, newHandle);
      }

      const hidden = this.querySelector(
        `[data-dev-bundle-variant-hidden][data-dev-bundle-component-index="${compIndex}"]`
      );
      const hasOnlyDefault =
        newProduct.variants?.length === 1 && newProduct.variants[0]?.title === 'Default Title';
      let optionsContainer = card.querySelector('[data-dev-bundle-options-container]');

      if (hasOnlyDefault) {
        if (optionsContainer) optionsContainer.innerHTML = '';
        if (hidden instanceof HTMLInputElement) hidden.value = String(newProduct.variants[0].id);
        this.#updatePicker(card);
        return;
      }

      if (!(optionsContainer instanceof HTMLElement)) return;
      const parentId = this.dataset.productId || this.#config?.productId || '0';
      optionsContainer.innerHTML = this.#buildOptionsHtml(
        newProduct,
        String(compIndex),
        String(parentId),
        this.#shouldSkipColorOptions(card)
      );

      if (previousSelections) {
        for (const [optKey, desiredVal] of Object.entries(previousSelections)) {
          const optIdx = String(Number(optKey.replace('option', '')) - 1);
          const radios = optionsContainer.querySelectorAll(
            `[data-dev-bundle-option-radio][data-dev-bundle-option-index="${optIdx}"]`
          );
          for (const r of radios) {
            if (r instanceof HTMLInputElement && r.value === desiredVal) {
              r.checked = true;
              break;
            }
          }
        }
      }

      const signal = this.#abortController?.signal;
      for (const r of optionsContainer.querySelectorAll('[data-dev-bundle-option-radio]')) {
        r.addEventListener('change', () => this.#updatePicker(card), { signal });
      }
      this.#updatePicker(card);
    } catch {
      this.#showError(Theme.translations?.add_to_cart_error || 'Unable to load this color option.');
    }
  }

  /**
   * Color option pills stay hidden when color siblings drive the color UX.
   * Prefer the Liquid flag (`data-dev-bundle-skip-color`); fall back to counting swatch buttons
   * (not the wrapper's `.children` — that is usually a single `.cs-wrap`).
   *
   * @param {HTMLElement} card
   * @returns {boolean}
   */
  #shouldSkipColorOptions(card) {
    if (card.hasAttribute('data-dev-bundle-skip-color')) return true;
    return card.querySelectorAll('[data-dev-bundle-swatch]').length > 1;
  }

  /**
   * @param {any} newProduct
   * @param {string} compIndex
   * @param {string} parentId
   * @param {boolean} skipColor
   * @returns {string}
   */
  #buildOptionsHtml(newProduct, compIndex, parentId, skipColor) {
    let html = '';
    const options = newProduct.options ?? [];

    for (let optIdx = 0; optIdx < options.length; optIdx++) {
      const raw = options[optIdx];
      const optName = (typeof raw === 'string' ? raw : raw?.name) ?? '';
      if (skipColor && isColorOption(optName)) continue;

      const valuesSet = new Set();
      const orderedValues = [];
      for (const v of newProduct.variants ?? []) {
        const val = optIdx === 0 ? v.option1 : optIdx === 1 ? v.option2 : v.option3;
        if (val && !valuesSet.has(val)) {
          valuesSet.add(val);
          orderedValues.push(val);
        }
      }
      if (!orderedValues.length) continue;

      html += `<fieldset class="dev-bundle-card__option-group" data-dev-bundle-option-group data-dev-bundle-option-index="${optIdx}">`;
      html += `<legend class="dev-bundle-card__option-label">${this.#escapeHtml(optName)}</legend>`;
      html += `<div class="dev-bundle-card__option-values" data-dev-bundle-option-values data-dev-bundle-option-index="${optIdx}">`;

      orderedValues.forEach((val, valIdx) => {
        const radioId = `dev-bundle-${parentId}-c${compIndex}-o${optIdx}-v${valIdx + 1}-${Date.now()}`;
        const name = `dev-bundle-${parentId}-c${compIndex}-o${optIdx}`;
        const escaped = this.#escapeAttr(val);
        html += `<input type="radio" class="visually-hidden" name="${name}" id="${radioId}" value="${escaped}" data-dev-bundle-option-radio data-dev-bundle-component-index="${compIndex}" data-dev-bundle-option-index="${optIdx}" data-dev-bundle-option-value="${escaped}"${valIdx === 0 ? ' checked' : ''}>`;
        html += `<label class="dev-bundle-card__option-swatch" for="${radioId}"><span>${this.#escapeHtml(val)}</span></label>`;
      });

      html += '</div></fieldset>';
    }
    return html;
  }

  /**
   * @param {string} str
   * @returns {string}
   */
  #escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /**
   * @param {string} str
   * @returns {string}
   */
  #escapeAttr(str) {
    return String(str).replace(/"/g, '&quot;').replace(/</g, '&lt;');
  }

  async #addToCart() {
    const config = this.#config;
    if (!config) return;

    const atc = this.querySelector('[data-dev-bundle-atc]');
    if (
      atc instanceof HTMLButtonElement &&
      (atc.getAttribute('aria-busy') === 'true' || atc.hasAttribute('data-dev-bundle-atc-disabled') || atc.disabled)
    ) {
      return;
    }

    const stock = this.#refreshStock({ showMessage: true });
    if (!stock.ok) return;

    this.#showError('');
    this.#setLoading(true);

    const bundleId = `${config.productId}-${Date.now()}`;
    const discountPercent = String(config.discountPercent || 0);
    /** @type {Record<string, string>} */
    const baseProps = {
      _bundle_id: bundleId,
      _bundle_title: config.title,
      _bundle_handle: config.handle,
      _bundle_discount_percent: discountPercent,
    };
    if (config.featuredImage) baseProps._bundle_image = config.featuredImage;

    /** @type {Array<{id: number, quantity: number, properties: Record<string, string>}>} */
    const items = [];

    for (let i = 0; i < config.components.length; i++) {
      const c = config.components[i];
      if (!c?.product_handle) continue;

      const card = this.querySelector(`[data-dev-bundle-card][data-dev-bundle-component-index="${i}"]`);
      if (!(card instanceof HTMLElement)) {
        // Prestige parity: missing Liquid card ends as a selection-style error (not sold out)
        const title = c.label || c.product_handle;
        this.#showError(
          this.#fillTemplate(this.dataset.selectVariantForLabel || '', { title }) ||
            this.dataset.selectVariantLabel ||
            `Select a variant for: ${title}`
        );
        this.#setLoading(false);
        return;
      }

      const hidden = this.querySelector(`[data-dev-bundle-variant-hidden][data-dev-bundle-component-index="${i}"]`);
      if (!(hidden instanceof HTMLInputElement) || !hidden.value) {
        const title = this.#componentTitle(card, c);
        this.#showError(
          this.#fillTemplate(this.dataset.selectVariantForLabel || '', { title }) ||
            this.dataset.selectVariantLabel ||
            'Select a variant'
        );
        this.#setLoading(false);
        return;
      }
      const componentQty = Math.max(1, Math.floor(Number(c.quantity) || 1));
      items.push({
        id: Number(hidden.value),
        quantity: componentQty,
        properties: {
          ...baseProps,
          _bundle_component_qty: String(componentQty),
        },
      });
    }

    const sectionIds = [];
    for (const el of document.querySelectorAll('cart-items-component')) {
      if (el instanceof HTMLElement && el.dataset.sectionId) {
        sectionIds.push(el.dataset.sectionId);
      }
    }

    const addUrl =
      (typeof Theme !== 'undefined' && Theme.routes?.cart_add_url) ||
      `${window.Shopify?.routes?.root || '/'}cart/add.js`;

    try {
      const res = await fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items,
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const data = await res.json();

      if (data.status === 422 || data.message) {
        this.#showError(data.description || data.message || Theme.translations?.add_to_cart_error || 'Error');
        if (data.status === 422) this.#setButtonOutOfStock(true);
        this.#setLoading(false);
        return;
      }

      this.#setLoading(false);

      const addedItems = Array.isArray(data.items) ? data.items : Array.isArray(data) ? data : [];
      if (typeof window.bundleHandleAddToCart === 'function' && addedItems.length) {
        window.bundleHandleAddToCart(addedItems, config.title);
      }

      document.dispatchEvent(
        new CartAddEvent(data, `dev-bundle-${config.productId}`, {
          source: 'dev-bundle-configurator',
          itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
          productId: String(config.productId),
          sections: data.sections,
        })
      );

      const drawer = document.querySelector('cart-drawer-component');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
      }
    } catch {
      this.#showError(Theme.translations?.add_to_cart_error || 'Connection error.');
      this.#setLoading(false);
    }
  }
}

if (!customElements.get('dev-bundle-configurator')) {
  customElements.define('dev-bundle-configurator', DevBundleConfigurator);
}
